import { LockFilled } from '@ant-design/icons';
import { ChevronLeftIcon } from '@heroicons/react/outline';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Radio,
  Skeleton,
  Space,
  Steps,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Logo } from 'assets';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import mixpanel from 'mixpanel-browser';
import { GetServerSideProps } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import { MdOutlineMyLocation } from "react-icons/md";
import IntlTelInput from 'react-intl-tel-input';
import { useRecoilState, useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import 'react-phone-number-input/style.css';
import 'react-intl-tel-input/dist/main.css';

import { useAuth } from '@/hooks/useAuth';
import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';
import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import { formatCartData } from '@/components/CartPage';
import AddressModal from '@/components/CheckoutV2/Components/AddressModal';
import CancelPaymentModal from '@/components/CheckoutV2/Components/CancelPaymentModal';
import CheckoutV2Card from '@/components/CheckoutV2/Components/Card';
import PaymentAvailability from '@/components/CheckoutV2/Components/PaymentAvailability';
import ResponsiveSelect from '@/components/CheckoutV2/Components/ResponsiveSelect';
import StickyPayButton from '@/components/CheckoutV2/Components/StickyPayButton';
import RenderIf from '@/components/Common/RenderIf';
import { montserrat } from '@/components/Layout';
import { CountryData } from '@/components/Layout/CountrySelector';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { getUserId } from '@/components/shared/getToken';
import { LoadingV2 } from '@/components/shared/LoadingV2';
import { notify } from '@/components/shared/notify';

import { authModalControl } from '@/atoms/authModalControl';
import { countryAtom } from '@/atoms/countryState';
import { localUserID, user } from '@/atoms/user';
import appwriteService from '@/config/appwrite';
import { Endpoints } from '@/network';
import {
  createAddressService,
  getAddresses,
  getAllCountries,
  getCities,
  getStates,
  ICerateAddress,
  updateAddress,
  updateDefaultAddress,
} from '@/services/Address';
import { apiClient } from '@/services/apiClients';
import {
  addCouponCode,
  checkEmailExist,
  getActiveCountries,
  getCartCount,
  getCartDetails,
  getCartSummary,
  getTempCartUser,
  handleConvertToCod,
  handlePaymentFailed,
  handleRemoveCoupon,
  placeOrder,
  placePreOrder,
  syncCart,
  syncOthersCache,
  tempCartUsers,
  updateCartAddress,
} from '@/services/Cart';
import { getSyncBookListApi } from '@/services/Library/Books';
import { EventV2, IEvent } from '@/utils/Events/events';
import { globalCountryChange } from '@/utils/GlobalCountry';
import { ICities } from '@/utils/interfaces/Cities';
import {
  Address,
  AddressPayload,
  CountryType,
} from '@/utils/interfaces/Manage-Addresses';
import { IStates } from '@/utils/interfaces/States';
import { User } from '@/utils/interfaces/User';

interface CheckoutV2Props {
  cartId: string;
  buyNow: string;
  cn?: string;
}

export const CheckoutMetadataLocalStorageKey = 'checkout_metadata'

const CheckoutV2: React.FC<CheckoutV2Props> = ({ cartId, buyNow, cn }) => {
  const twScreen = useTailwindBreakpoint();
  const router = useRouter();
  const idBuyNow = buyNow === 'true'

  const [form] = Form.useForm();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [getCountriesLoading, setGetCountriesLoading] =
    useState<boolean>(false);
  const [getStatesLoading, setGetStatesLoading] = useState<boolean>(false);
  const [getCitiesLoading, setGetCitiesLoading] = useState<boolean>(false);
  const [getBillingCountriesLoading, setGetBillingCountriesLoading] =
    useState<boolean>(false);
  const [getBillingStatesLoading, setGetBillingStatesLoading] =
    useState<boolean>(false);
  const [getBillingCitiesLoading, setGetBillingCitiesLoading] =
    useState<boolean>(false);
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [states, setStates] = useState<IStates[]>([]);
  const [cities, setCities] = useState<ICities[]>([]);
  const [billingCountries, setBillingCountries] = useState<CountryType[]>([]);
  const [billingStates, setBillingStates] = useState<IStates[]>([]);
  const [billingCities, setBillingCities] = useState<ICities[]>([]);
  // const [detectingLocation, setDetectingLocation] = useState<boolean>(false);
  const [cart, setCart] = useState<any>(undefined);
  const [coupon, setCoupon] = useState<string>('');
  const [cartSummary, setCartSummary] = useState<any>();
  const [, setAuthModal] = useRecoilState(authModalControl);
  const { onSignUp } = useAuth();
  const userData: User | undefined = useRecoilValue(user);
  // const billingSameAsSipping = Form.useWatch('billingSameAsSipping', form);
  const isGst = Form.useWatch('isGst', form);
  const paymentOption = Form.useWatch('paymentOption', form);

  const hasCodDisabledItem: boolean = useMemo(() => {
    const items =
      cart?.data?.flatMap((group: any) => group?.cart_items || []) || [];
    return items.some(
      (item: any) =>
        item?.cash_on_delivery === false ||
        item?.cash_on_delivery === 'false' ||
        item?.i18n_attributes?.cash_on_delivery === false ||
        item?.i18n_attributes?.cash_on_delivery === 'false',
    );
  }, [cart]);

  const isINR = (cartSummary?.currency_code || '').toUpperCase() === 'INR';
  const grandTotalNum = Number(String(cartSummary?.grand_total || '0').replace(/,/g, ''));
  const hideCOD = isINR && grandTotalNum > 12000;

  useEffect(() => {
    if ((hideCOD || hasCodDisabledItem) && paymentOption === 'cod') {
      form.setFieldValue('paymentOption', 'online');
    }
  }, [hideCOD, hasCodDisabledItem, paymentOption, form]);

  const watchedCountry = Form.useWatch('country', form);
  const watchedCountryId = Form.useWatch('countryId', form);
  const watchedStateId = Form.useWatch('stateId', form);
  const watchedBillingCountryId = Form.useWatch('billingCountryId', form);
  const watchedBillingStateId = Form.useWatch('billingStateId', form);
  const watchedEmail = Form.useWatch('email', form);
  const [addresses, setAddresses] = useState([]);
  const [address, setAddress] = useState<any>();
  const [billingAddress, setBillingAddress] = useState<any>();
  const [addressModal, setAddressModal] = useState<{
    show: boolean;
    currentClickType: 'edit' | 'create' | null;
    addressForUpdate: Address | null;
  }>({ show: false, currentClickType: null, addressForUpdate: null });
  const [billingAddressModal, setBillingAddressModal] = useState<{
    show: boolean;
    currentClickType: 'edit' | 'create' | null;
    addressForUpdate: Address | null;
  }>({ show: false, currentClickType: null, addressForUpdate: null });
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentProcessing, setPaymentProcessing] = useState<boolean>(false);
  const [couponLoading, setcCouponLoading] = useState<boolean>(false);
  const [cartDetailsLoading, setCartDetailsLoading] = useState<boolean>(true);
  const [loadingRemoveCoupon, setLoadingRemoveCoupon] =
    useState<boolean>(false);
  const [loadingAddress, setLoadingAddress] = useState<boolean>(false);
  const [retryOrderData, setRetryOrderData] = useState<any>();
  const [retryProductIds, setRetryProductIds] = useState<any>('');
  const [retryPaymentOption, setRetryPaymentOption] = useState<any>('');
  const [cancelPaymentModal, setCancelPaymentModal] = useState<boolean>(false);
  const [cartSummaryLoading, setCartSummaryLoading] = useState<boolean>(true);
  const [convertCodLoading, setConvertCodLoading] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState<boolean>(false);
  const isPreOrder: boolean = cart?.data?.[0]?.cart_items?.some(
    (item: any) => item.is_preorder === true,
  );
  const [countryList, setCountryList] = useState<CountryData[]>([]);
  const localUserId = useRecoilValue(localUserID);
  const [countryState, setCountryState] = useRecoilState(countryAtom);
  // Tracks IntlTelInput's built-in per-country validity flag
  const phoneValidRef = useRef<boolean>(true);
  // Keeps a stable ref to the `cn` prop so URL-sync functions always see the latest value
  const cnRef = useRef<string | undefined>(cn);
  useEffect(() => { cnRef.current = cn; }, [cn]);
  const lastPrefilledPasswordRef = useRef<string>('');
  const hasSyncedDefaultCountryRef = useRef<boolean>(false);

  const isSelectedCountryIndia = useCallback(
    (countryVal?: string, countryIdVal?: number | string) => {
      if (countryVal && String(countryVal).trim().toLowerCase() === 'india') {
        return true;
      }
      if (countryIdVal) {
        const c = countries.find(
          (item) => String(item.id) === String(countryIdVal),
        );
        if (
          c?.code?.toUpperCase() === 'IN' ||
          c?.name?.toLowerCase() === 'india'
        ) {
          return true;
        }
      }
      if (!countryVal && !countryIdVal) {
        return countryState?.selectedCountry?.code?.toUpperCase() === 'IN';
      }
      return false;
    },
    [countries, countryState?.selectedCountry],
  );

  const isShippingIndia = useMemo(() => {
    return isSelectedCountryIndia(watchedCountry, watchedCountryId);
  }, [watchedCountry, watchedCountryId, isSelectedCountryIndia]);

  // ─── Redirect back-from-Stripe users to /my-orders ───────────────────────
  // Before redirecting to Stripe we set `stripe_redirect_pending` in
  // sessionStorage.  When the user presses Back they can land here in two ways:
  //
  //   1. Normal back navigation  → useEffect runs, we catch the flag.
  //   2. bfcache restore (mobile back-swipe / desktop back button that thaws a
  //      frozen page) → useEffect does NOT re-run.  The `pageshow` event always
  //      fires though, and `event.persisted === true` signals a bfcache hit.
  //
  // We handle both cases so the checkout page is never shown as a ghost.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const redirectIfStripe = () => {
      const flag = sessionStorage.getItem('stripe_redirect_pending');
      if (flag === 'true') {
        sessionStorage.removeItem('stripe_redirect_pending');
        router.replace('/my-orders');
      }
    };

    // Case 1 – normal mount / hard navigation back
    redirectIfStripe();

    // Case 2 – bfcache restore (page thawed from cache, useEffect skipped)
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        redirectIfStripe();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);
  // Ref that mirrors paymentProcessing state but is updated synchronously.
  // Used by navigation guards so they read the correct value even before
  // the async setState has re-rendered.
  const paymentProcessingRef = useRef<boolean>(false);

  // ─── Apply country from `cn` URL param (e.g. when URL is shared) ─────────────
  // Phase 1 (synchronous): immediately write localStorage + cookie so that
  // getGlobalCountry() — which is called by the Axios interceptor on every
  // request — returns the correct country code RIGHT AWAY, before any API
  // call fires. This prevents the cart-mismatch the backend sees when the
  // header country doesn't match the cart country.
  //
  // Phase 2 (async): fetch full country data and update Recoil state so that
  // all UI components (currency display, payment option, etc.) reflect the
  // correct country too.
  useEffect(() => {
    if (!cn || typeof window === 'undefined') return;

    // ── Phase 1: synchronous ────────────────────────────────────────────────
    const upperCn = cn.toUpperCase();
    // Write the bare minimum so getGlobalCountry() picks it up immediately.
    // We only override when the stored country differs from cn, to avoid
    // wiping out a richer entry that is already correct.
    const existingRaw = localStorage.getItem('zebra_selected_country');
    let existingCode: string | null = null;
    try {
      const parsed = JSON.parse(existingRaw || '');
      existingCode = (parsed?.country?.code || parsed?.code || null)?.toUpperCase() ?? null;
    } catch (e: any) {
      console.error(e)
    }

    if (existingCode !== upperCn) {
      // Store in the format that getGlobalCountry() in countryState/index.ts
      // expects: { country: { code, ... }, source }.
      localStorage.setItem(
        'zebra_selected_country',
        JSON.stringify({ country: { code: upperCn }, source: 'manual' }),
      );
    }
    document.cookie = `country=${upperCn}; path=/; max-age=${60 * 60 * 24}; SameSite=Lax`;

    // ── Phase 2: async — get full country data and update Recoil ───────────
    (async () => {
      try {
        const { getActiveCountries } = await import('@/services/Cart');
        const countryListData = await getActiveCountries();
        const fallback = countryListData.find((c: any) => c.code === 'IN') || null;
        const matched =
          countryListData.find(
            (c: any) => c.code.toUpperCase() === upperCn,
          ) || fallback;
        if (matched) {
          setCountryState({
            isOpen: false,
            selectedCountry: {
              ...matched,
              currency: matched.currency || 'INR',
              currency_symbol: matched.currency_symbol || '₹',
              timezone: matched.timezone || 'Asia/Kolkata',
            },
            source: 'manual',
          });
          // Overwrite with the full country object now that we have it
          localStorage.setItem(
            'zebra_selected_country',
            JSON.stringify({ country: matched, source: 'manual' }),
          );
          document.cookie = `country=${matched.code}; path=/; max-age=${60 * 60 * 24
            }; SameSite=Lax`;
        }
      } catch (err) {
        console.error('Failed to apply cn country from URL:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cn]);



  const getPasswordStrength = (value: string): 0 | 1 | 2 | 3 | 4 => {
    if (!value) return 0;
    let score = 0;
    if (value.length >= 8) { score = score + 4 };
    return score as 0 | 1 | 2 | 3 | 4;
  };

  const passwordStrengthConfig = [
    null,
    { label: 'Weak', color: '#ef4444', bars: 1 },
    { label: 'Fair', color: '#f97316', bars: 2 },
    { label: 'Strong', color: '#22c55e', bars: 3 },
    { label: 'Very Strong', color: '#16a34a', bars: 4 },
  ] as const;


  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields || errorFields.length === 0) return;

    const firstErrorFieldName = String(errorFields[0]?.name?.[0] ?? '');
    if (!firstErrorFieldName) return;

    // Map field names to their anchor wrapper div IDs
    const fieldAnchorMap: Record<string, string> = {
      email: 'form-anchor-email',
      fullname: 'form-anchor-fullname',
      phone: 'form-anchor-phone',
      password: 'form-anchor-password',
      address: 'form-anchor-address',
      country: 'form-anchor-country',
      pincode: 'form-anchor-pincode',
      state: 'form-anchor-state',
      city: 'form-anchor-city',
      billingAddress: 'form-anchor-billingAddress',
      billingCountry: 'form-anchor-billingCountry',
      billingPincode: 'form-anchor-billingPincode',
      billingState: 'form-anchor-billingState',
      billingCity: 'form-anchor-billingCity',
    };

    const anchorId = fieldAnchorMap[firstErrorFieldName];
    const el: HTMLElement | null =
      (anchorId ? document.getElementById(anchorId) : null) ||
      document.getElementById(`checkout-v2_${firstErrorFieldName}`) ||
      document.querySelector(`[name="${firstErrorFieldName}"]`) ||
      (document.querySelector('.checkout-v2-item-has-error') as HTMLElement | null);

    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top, behavior: 'smooth' });
      setTimeout(() => {
        try {
          const focusable = el.querySelector<HTMLElement>('input, textarea, select');
          (focusable ?? el).focus();
        } catch (e: any) { console.error(e) }
      }, 400);
    }
  };

  const checkoutNitroEvent = async () => {
    try {

      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.checkout === 'function') {
        const currentUrl = window.location.href;
        ;
        const zebra_selected_country = JSON?.parse(
          localStorage?.getItem?.('zebra_selected_country') as string,
        )?.value?.country;
        const utmData: Record<string, string> = {};
        const searchParams = new URLSearchParams(window?.location?.search);
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) {
            utmData[key] = value;
          }
        });
        const checkoutData = {
          checkout_url: currentUrl,
          coupon_code: coupon,
          payment_type: form.getFieldValue('paymentOption') === 'online' ? 'razorpay' : 'cash_on_delivery',
          cart_id: cart?.cart_id,
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          items: cart?.data?.[0]?.cart_items?.map((i: any) => ({
            product_id: i?.product_id,
            title: i?.display_name,
            quantity: i?.quantity,
            price: i?.price,
            currency: zebra_selected_country?.currency,
            image_url: i?.product_thumbnail_image,
            product_url: `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/books/temp-${i?.product_id}`
          })),
          cart_value: parseFloat(String(cartSummary?.grand_total ?? '0').replace(/,/g, '')),
          discount_amount: parseFloat(totalDiscount.toFixed(2).replace(/,/g, '')),
          utm_data: utmData,
        };

        nitro.checkout(checkoutData);
        ;
      }
    } catch (error) {
      console.error(error);
    }
  };
  const buyNitroEvent = async (orderId: number) => {
    try {

      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.buy === 'function') {
        const currentUrl = window.location.href;
        ;
        const zebra_selected_country = JSON?.parse(
          localStorage?.getItem?.('zebra_selected_country') as string,
        )?.value?.country;
        // const utmData: Record<string, string> = {};
        // const searchParams = new URLSearchParams(window?.location?.search);
        // searchParams.forEach((value, key) => {
        //   if (key.startsWith('utm_')) {
        //     utmData[key] = value;
        //   }
        // });
        const buyData = {
          order_id: orderId,//
          checkout_url: currentUrl,//
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          items: cart?.data?.[0]?.cart_items?.map((i: any) => ({
            product_id: i?.product_id,
            title: i?.display_name,
            price: i?.price,
            currency: zebra_selected_country?.currency,
            product_url: `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/books/temp-${i?.product_id}`
          })),
          // utm_data: utmData,
        };

        nitro.buy(buyData);
        ;
      }
    } catch (error) {
      console.error(error);
    }
  };


  const onFinish = async (values: any) => {
    checkoutNitroEvent();
    setLoading(true);

    console.log({ address })



    const meta = {
      payment_mode: paymentOption === 'online' ? 'prepaid' : 'cod',
      coupon: cartSummary?.coupon_code?.[0]?.coupon_code || '',
      discount: cartSummary?.discount,
      currency: cartSummary?.currency_code,
      country: countries?.find(i => i?.id === values?.countryId)?.name,
      state: states?.find(i => i?.id === values?.stateId)?.name,
      city: cities?.find(i => i?.id === values?.cityId)?.name,
      postal_code: values?.pincode || address.postal_code
    }

    const eventParams: IEvent = {
      event_name: 'begin_checkout',
      source: 'global',
      source_type: 'cart',
      source_id: cart?.cart_id?.toString(),
      sub_source: null,
      sub_source_id: null,
      unit: cartSummary?.currency_code || 'INR',
      currency: cartSummary?.currency_code || 'INR',
      value: cartSummary?.grand_total || 0,
      meta: meta,
    };

    EventV2({ payload: eventParams, userData, pathname: router.pathname as string, })

    localStorage.setItem(CheckoutMetadataLocalStorageKey, JSON.stringify((meta)))

    try {
      if (!isAuthenticated) {

        const isEmailExists = await checkEmailExistHandle(values.email)

        if (isEmailExists) {
          setEmailAlreadyExists(true);
          setAuthModal((prevState) => ({
            ...prevState,
            login: true,
            email: values.email,
            emailExistsMessage: true,
          }));
          return;
        }


        await onSignUp({
          email: values.email,
          password: values.password,
          name: values.fullname,
          phone: values.phone,
          phoneCountryCode: values.phoneCountryCode || 'in',
          phonePrefix: values.phonePrefix || '91',
          isModal: true,
        });


        checkIsAuthenticated()
        notify('Account created successfully', 'success');
      }

      const userId = await getUserId();

      if (!userId) {
        throw new Error('userId not found');
      }

      // if (!values.countryId) {
      //   throw new Error('countryId not found');
      // }

      // if (!values.stateId) {
      //   throw new Error('stateId not found');
      // }

      // if (!values.cityId) {
      //   throw new Error('stateId not found');
      // }

      let shippingAddress = { data: address };

      if (addressModal.currentClickType === 'create' || !isAuthenticated || addresses?.length === 0) {
        shippingAddress = await createAddressHandle({
          address: values.address,
          city_id: values.cityId,
          country_id: values.countryId,
          name: values.fullname,
          phone: values.phone,
          postal_code: values.pincode,
          set_default: 1,
          state_id: values.stateId,
          user_id: parseInt(userId),
          country_code: values.phoneCountryCode || 'in',
          iso2: values.phoneCountryCode || 'in',
        });
        // notify('Address created successfully', 'success');
      }

      if (addressModal.currentClickType === 'edit' && isAuthenticated) {
        const updateData = {
          id: addressModal.addressForUpdate?.id,
          address: values.address,
          city_id: values.cityId,
          country_id: values.countryId,
          // name: values.fullname,
          phone: values.phone,
          postal_code: values.pincode,
          set_default: 1,
          state_id: values.stateId,
          user_id: parseInt(userId),
          country_code: values.phoneCountryCode || 'in',
          iso2: values.phoneCountryCode || 'in',
        };
        shippingAddress = await updateAddressHandle(updateData);
        // notify('Address edited successfully', 'success');
        shippingAddress = { data: updateData };
      }

      // let billingAddressId = billingAddress?.id;

      // if (billingSameAsSipping) {
      //   billingAddressId = shippingAddress?.data?.id;
      // }

      // if (!billingSameAsSipping) {
      //   if (
      //     billingAddressModal.currentClickType === 'create' ||
      //     !isAuthenticated || addresses?.length === 0
      //   ) {
      //     const billingAddress = await createAddressHandle({
      //       address: values.billingAddress,
      //       city_id: values.billingCityId,
      //       country_id: values.billingCountryId,
      //       name: values.fullname,
      //       phone: values.phone,
      //       postal_code: values.billingPincode,
      //       set_default: 1,
      //       state_id: values.billingStateId,
      //       user_id: parseInt(userId),
      //       country_code: values.phoneCountryCode || 'in',
      //       iso2: values.phoneCountryCode || 'in',
      //     });
      //     billingAddressId = billingAddress?.data?.id;
      //     // notify('Billing address edited successfully', 'success');
      //   }

      //   if (
      //     billingAddressModal.currentClickType === 'edit' &&
      //     isAuthenticated
      //   ) {
      //     const updateData = {
      //       address: values.billingAddress,
      //       city_id: values.billingCityId,
      //       country_id: values.billingCountryId,
      //       name: values.fullname,
      //       phone: values.phone,
      //       postal_code: values.billingPincode,
      //       set_default: 1,
      //       state_id: values.billingStateId,
      //       user_id: parseInt(userId),
      //       country_code: values.phoneCountryCode || 'in',
      //       iso2: values.phoneCountryCode || 'in',
      //       id: billingAddressModal.addressForUpdate?.id,
      //     };
      //     await updateAddressHandle(updateData);
      //     // notify('Billing address edited successfully', 'success');
      //     billingAddressId = billingAddressModal.addressForUpdate?.id;
      //   }
      // }

      const localStorageUserId = localStorage.getItem('userId');

      if (!localStorageUserId) {
        throw new Error('localStorageUserId not found');
      }

      await syncCartHandle({
        temp_user_id: localStorageUserId,
        user_id: userId,
        cart_id: cart?.cart_id,
      });
      // notify('user assigned successfully', 'success');

      const cartDetails = await getCartDetailsFromUserId(userId);
      // notify('Cart detailes recieved successfully', 'success');

      const cartId = cartDetails?.data?.cart_id;

      await updateCartAddressHandle(
        shippingAddress?.data?.id,
        cartId,
        shippingAddress?.data?.id,
        values.gstNumber,
      );
      // notify('Addresses assigned to cart successfully', 'success');

      const onlinePaymentOptions: 'razorpay' | 'razorpay_2' | 'stripe' =
        countryState.selectedCountry?.code === 'IN' ? 'razorpay'
          : process.env.NEXT_PUBLIC_USE_STRIPE === 'true' ? 'stripe'
            : 'razorpay_2';

      const paymentType: 'razorpay' | 'razorpay_2' | 'cash_on_delivery' | 'stripe' =
        values.paymentOption === 'online'
          ? onlinePaymentOptions
          : 'cash_on_delivery';

      setRetryPaymentOption(paymentType);

      let order;
      if (isPreOrder) {
        order = await placePreOrderHandle(paymentType, cartId);
      } else {
        order = await placeOrderHandle(
          paymentType,
          cartId,
          shippingAddress?.data?.id,
          values.gstNumber,
          shippingAddress?.data?.id,
        );
      }
      if (process.env.NEXT_PUBLIC_USE_STRIPE === 'true' && paymentType === 'stripe') {
        null
      } else {
        notify('Order placed successfully', 'success');
      }


      setRetryOrderData(order);

      const productIds = (order?.data?.order_details || [])
        .map((detail: any) => detail?.product?.id)
        .join(',');

      setRetryProductIds(productIds);

      await handleCartCount(cartId);

      if (paymentType === 'stripe') {
        const stripePaymentUrl = order?.data?.payment_details?.payment_url;
        if (stripePaymentUrl) {
          // Mark that we are leaving for Stripe so that if the user presses
          // the browser Back button they get redirected to /my-orders instead
          // of landing back on the checkout form.
          sessionStorage.setItem('stripe_redirect_pending', 'true');
          window.location.href = stripePaymentUrl;
        }
        return;
      }

      if (order?.data?.payment_status === 'paid') {
        getSyncBookListApi();
        buyNitroEvent(order?.data?.id)
        if (typeof window !== 'undefined') {
          router.replace({
            pathname: '/order-successful',
            query: {
              orderId: order?.data?.id,
              product_ids: productIds,
              callPurchased: 'purchased',
            },
          });

          window.history.replaceState(null, '', '/order-successful');
        }
        return;
      }

      if (paymentType === 'cash_on_delivery') {
        buyNitroEvent(order?.data?.id)
        setLoading(false);
        if (typeof window !== 'undefined') {
          router.replace({
            pathname: '/order-successful',
            query: {
              orderId: order?.data?.id,
              product_ids: productIds,
              callPurchased: 'purchased',
            },
          });
          window.history.replaceState(null, '', '/order-successful');
        }
        return;
      }

      if (paymentType === 'razorpay' || paymentType === 'razorpay_2') {
        paymentProcessingRef.current = true;
        setPaymentProcessing(true)
        razorpayHandle({
          key:
            paymentType === 'razorpay_2'
              ? process.env.NEXT_PUBLIC_RAZORPAY_KEY_2!
              : process.env.NEXT_PUBLIC_RAZORPAY_KEY!,
          amount: cartSummary?.grand_total,
          currency: 'INR',
          handler: () => {
            // Synchronously disable the navigation guard BEFORE redirecting,
            // so the routeChangeStart listener does not intercept the success redirect.
            paymentProcessingRef.current = false;
            setPaymentProcessing(false);
            getSyncBookListApi();
            if (typeof window !== 'undefined') {
              buyNitroEvent(order?.data?.id)
              router.replace({
                pathname: '/order-successful',
                query: {
                  orderId: order?.data?.id,
                  product_ids: productIds,
                  callPurchased: 'purchased',
                },
              });
            }
          },
          image: Logo,
          modal: {
            escape: false,
            ondismiss: async () => {
              paymentProcessingRef.current = false;
              setPaymentProcessing(false);
              setCancelPaymentModal(true);
              const rzpOrderId = order?.data?.payment_details?.rzp_order_id;
              if (rzpOrderId) {
                await paymentFailed({
                  orderId: order?.data?.id?.toString() || '',
                  razorpayOrderId: rzpOrderId,
                  cartId: Number(cartId || 0),
                  currencyCode: cartSummary?.currency_code || 'INR',
                  totalAmount: Number(cartSummary?.grand_total || 0),
                });
              }
            },
          },
          name: 'ZebraLearn',
          order_id: order?.data?.payment_details?.rzp_order_id,
          prefill: {
            ...(userData?.name || values?.fullname ? { name: userData?.name || values?.fullname } : {}),
            email: userData?.email || values.email,
            // currency: 'INR',
            contact: userData?.phone || values.phone,
          },
          onPaymentFailed: () => {
            paymentProcessingRef.current = false;
            setPaymentProcessing(false);
            const rzpOrderId = order?.data?.payment_details?.rzp_order_id;
            if (rzpOrderId) {
              paymentFailed({
                orderId: order?.data?.id?.toString() || '',
                razorpayOrderId: rzpOrderId,
                cartId: Number(cartId || 0),
                currencyCode: cartSummary?.currency_code || 'INR',
                totalAmount: Number(cartSummary?.grand_total || 0),
              });
            }
          },
        });
      }
    } catch (error: any) {
      console.error({ thisiserror: error.message });
      if (error.message == "Invalid `email` param: Value must be a valid email address") {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return;
      }
      notify(error.message, 'error');
      notify(formateErrorObject(error), 'error');

    } finally {
      setLoading(false);
      // Note: setPaymentProcessing(false) is intentionally NOT called here.
      // razorpayHandle returns immediately after opening the modal (rzp.open() is non-blocking).
      // Resetting here would cancel the navigation guard before payment completes.
      // Instead, each Razorpay callback (handler, ondismiss, onPaymentFailed) resets it.
    }
  };

  const paymentFailed = async ({ orderId, razorpayOrderId, cartId, currencyCode, totalAmount }: { orderId: string, razorpayOrderId: string, cartId: number, currencyCode: string, totalAmount: number }) => {
    try {
      await handlePaymentFailed(
        {
          order_id: orderId,
          razorpay_order_id: razorpayOrderId,
        },
        cartId ?? Number(cartId),
      );
      const eventParams: EventDetails = {
        event_name: 'order_failed',
        source: 'global',
        source_type: 'orders',
        source_id: orderId,
        sub_source: null,
        sub_source_id: null,
        unit: currencyCode || 'INR',
        currency: currencyCode || 'INR',
        value: totalAmount,
        meta: {
          payment_mode: 'prepaid',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname as string,
      });
    } catch (error: any) {
      // const message = formateErrorObject(error);
      // notify(message, 'error');
    }
  };

  const updateAddressHandle = async (data: AddressPayload, cartId?: number) => {
    const result = await updateAddress(data, cartId);
    return result;
  };

  const getAddressesHandle = async () => {
    try {
      setLoadingAddress(true);
      const result = await getAddresses();
      setAddresses(result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingAddress(false);
    }
  };

  const razorpayHandle = async ({
    key,
    amount,
    currency,
    name,
    image,
    order_id,
    handler,
    modal,
    prefill,
    onPaymentFailed,
  }: {
    key: string;
    amount: number;
    currency: string;
    name: string;
    image: any;
    order_id: string;
    handler: any;
    modal: any;
    prefill: any;
    onPaymentFailed?: (response: any) => void;
  }) => {
    try {
      if (typeof window !== 'undefined') {
        const options = {
          key: key,
          amount: amount,
          currency: currency,
          name: name,
          image: image,
          order_id: order_id,
          handler: handler,
          modal: modal,
          prefill: prefill,
        };

        if ((window as any).Razorpay) {
          const rzp = new (window as any).Razorpay(options);

          rzp.on('payment.failed', function (response: any) {
            console.error('Razorpay Payment Failed: ', response.error);
            notify(
              response.error?.description ||
              'Payment Failed. Please try again.',
              'error',
            );
            if (onPaymentFailed) {
              onPaymentFailed(response);
            }
          });

          rzp.open();

          // rzp.on('close', () => {
          //   window.removeEventListener('beforeunload', handleBeforeUnload);
          //   router.events.off('routeChangeComplete', manageBeforeUnloadListener);
          // });
        } else {
          console.error('Razorpay SDK not loaded');
        }
      } else {
        console.error(
          'Window object is undefined. Cannot initialize Razorpay.',
        );
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const retryOrderHandle = async () => {
    try {
      razorpayHandle({
        key:
          retryPaymentOption === 'razorpay_2'
            ? process.env.NEXT_PUBLIC_RAZORPAY_KEY_2!
            : process.env.NEXT_PUBLIC_RAZORPAY_KEY!,
        amount: cartSummary?.grand_total,
        currency: 'INR',
        handler: () => {
          getSyncBookListApi();
          buyNitroEvent(retryOrderData?.data?.id)
          if (typeof window !== 'undefined') {
            router.replace({
              pathname: '/order-successful',
              query: {
                orderId: retryOrderData?.data?.id,
                product_ids: retryProductIds,
                callPurchased: 'purchased',
              },
            });
          }
        },
        image: Logo,
        modal: {
          escape: false,
          ondismiss: async () => {
            setCancelPaymentModal(true);
            const rzpOrderId = retryOrderData?.data?.payment_details?.rzp_order_id;
            if (rzpOrderId) {
              await paymentFailed({
                orderId: retryOrderData?.data?.id?.toString() || '',
                razorpayOrderId: rzpOrderId,
                cartId: Number(cart?.cart_id || 0),
                currencyCode: cartSummary?.currency_code || 'INR',
                totalAmount: Number(cartSummary?.grand_total || 0),
              });
            }
          },
        },
        name: 'ZebraLearn',
        order_id: retryOrderData?.data?.payment_details?.rzp_order_id,
        prefill: {
          ...(userData?.name ? { name: userData?.name } : {}),
          email: userData?.email,
          // currency: 'INR',
          contact: userData?.phone,
        },
        onPaymentFailed: () => {
          const rzpOrderId = retryOrderData?.data?.payment_details?.rzp_order_id;
          if (rzpOrderId) {
            paymentFailed({
              orderId: retryOrderData?.data?.id?.toString() || '',
              razorpayOrderId: rzpOrderId,
              cartId: Number(cart?.cart_id || 0),
              currencyCode: cartSummary?.currency_code || 'INR',
              totalAmount: Number(cartSummary?.grand_total || 0),
            });
          }
        },
      });

      const eventParams: EventDetails = {
        event_name: 'retry_payment',
        source: 'global',
        source_type: 'orders',
        source_id: retryOrderData?.data?.id?.toString(),
        sub_source: null,
        sub_source_id: null,
        unit: retryOrderData?.data?.currency_code || 'INR',
        currency: retryOrderData?.data?.currency_code || 'INR',
        value: retryOrderData?.data?.grand_total,
        meta: {
          click_source: 'retry_popup',
          payment_mode: 'prepaid',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });

      if (typeof mixpanel !== 'undefined' && process.env.NEXT_PUBLIC_APP_ENV === 'production') {
        mixpanel.track(eventParams.event_name as string, eventParams);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onCodClickHandle = async () => {
    try {
      setConvertCodLoading(true);
      await handleConvertToCod(
        {
          order_id: retryOrderData?.data?.id as string,
        },
        Number(cart?.cart_id),
      );

      const eventParams: EventDetails = {
        event_name: 'convert_cod',
        source: 'global',
        source_type: 'orders',
        source_id: retryOrderData?.data?.id?.toString() as string,
        sub_source: null,
        sub_source_id: null,
        unit: retryOrderData?.data?.currency_code,
        currency: retryOrderData?.data?.currency_code,
        value: retryOrderData?.data?.grand_total,
        meta: {
          click_source: 'retry_popup',
          payment_mode: 'cod',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });

      if (typeof mixpanel !== 'undefined' && process?.env?.NEXT_PUBLIC_APP_ENV === 'production') {
        mixpanel.track(eventParams.event_name as string, eventParams);
      }
      buyNitroEvent(retryOrderData?.data?.id)
      if (typeof window !== 'undefined') {
        router.replace({
          pathname: '/order-successful',
          query: {
            orderId: retryOrderData?.data?.id,
            product_ids: retryProductIds,
            callPurchased: 'purchased',
          },
        });
        window.history.replaceState(null, '', '/order-successful');
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setConvertCodLoading(false);
    }
  };

  const handleCartCount = async (cartId: number) => {
    try {
      await getCartCount({ cartId });
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const placePreOrderHandle = async (payment_type: string, cartId?: number) => {
    const result = await placePreOrder(payment_type, cartId);
    return result;
  };

  const placeOrderHandle = async (
    payment_type: string,
    cartId?: number,
    billing_address_id?: number,
    billing_gst_number?: string,
    shipping_address_id?: number,
  ) => {
    try {
      const result = await placeOrder(
        payment_type,
        cartId,
        billing_address_id,
        billing_gst_number,
        shipping_address_id,
      );
      return result;
    } catch (error: any) {
      console.error("the errorrrr: ", error);
      if (error?.response?.data?.message) {
        notify(error?.response?.data?.message);
      }
      if (error?.response?.status === 500 || !error?.response?.data) {
        router.push('/my-orders')
      }
      throw error;
    }

  };

  const getCartDetailsFromUserId = async (userId: string) => {
    const result = await apiClient.post(Endpoints.getCartDetails, {
      user_id: userId,
    });

    return result;
  };

  const updateCartAddressHandle = async (
    shipping_address_id: number,
    cartId?: number,
    billing_address_id?: number,
    billing_gst_number?: string,
  ) => {
    await updateCartAddress(
      shipping_address_id,
      cartId,
      billing_address_id,
      billing_gst_number,
    );
  };
  const syncCartHandle = async (payload: {
    temp_user_id: string;
    user_id: string;
    cart_id: string;
  }) => {
    await syncCart(payload);
  };

  const content = 'This is a content.';

  const items = [
    {
      title: 'Pick product',
      content,
    },
    {
      title: 'Your cart',
      content,
    },
    {
      title: 'Checkout',
      content,
    },
  ];

  const createAddressHandle = async (data: ICerateAddress, cartId?: number) => {
    const result = await createAddressService(data, cartId);
    return result;
  };

  const checkIsAuthenticated = async () => {
    try {
      const result = await appwriteService.isLoggedIn();
      setIsAuthenticated(result);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    }
  };

  const getAllCountriesHandle = async () => {
    try {
      const result = await getAllCountries();
      return result.data;
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
      return null;
    }
  };

  const getStatesHandle = async (id: number) => {
    try {
      const result = await getStates(id);
      return result.data;
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
      return null;
    }
  };

  const getCitiesHandle = async (id: number) => {
    try {
      const result = await getCities(id);
      return result.data;
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
      return null;
    }
  };


  const fallbackCountry = useMemo<CountryData | null>(() => {
    return countryList.find((c) => c.code === 'US') || null;
  }, [countryList]);


  const onCountryChange = async (
    _: any,
    option?:
      | {
        lable: number;
        value: string;
      }
      | {
        lable: number;
        value: string;
      }[]
      | undefined,
  ) => {

    const selectedCountryName = (option as any)?.value;

    const selectedCountryCode = countries.find(i => i?.name === selectedCountryName)?.code;

    const selectedCounty = countryList?.find((c) => c?.code === selectedCountryCode);

    const selectedGlobalCountryCode = selectedCounty?.code || 'US'

    // Sync the new country code into the URL so sharing the URL preserves it
    if (typeof window !== 'undefined' && selectedGlobalCountryCode) {
      const url = new URL(window.location.href);
      url.searchParams.set('cn', selectedGlobalCountryCode || 'US');
      window.history.replaceState(null, '', url.toString());
    }

    await globalCountryChange({
      cartData: formatCartData(cart?.data?.[0]?.cart_items,
        cart?.currency_code,
        cart?.cart_id,),
      code: selectedGlobalCountryCode,
      countryList,
      fallbackCountry,
      localUserId,
      router,
      setCountryState,
      source: 'manual',
      userData
    });




    await globalCountryChangeHandle();

    form.setFieldValue('state', undefined);
    form.setFieldValue('stateId', undefined);
    form.setFieldValue('city', undefined);
    form.setFieldValue('cityId', undefined);

    const countryId = (
      option as {
        lable: number;
        value: string;
      }
    )?.lable as number;
    form.setFieldValue('countryId', countryId);
    await tempCartUsersHandle('countryId', countryId);
  };

  const onBillingCountryChange = async (
    _: any,
    option?:
      | {
        lable: number;
        value: string;
      }
      | {
        lable: number;
        value: string;
      }[]
      | undefined,
  ) => {
    // Reset dependent fields — the useEffect watcher on billingCountryId will fetch states automatically
    setBillingStates([]);
    setBillingCities([]);
    form.setFieldValue('billingState', undefined);
    form.setFieldValue('billingStateId', undefined);
    form.setFieldValue('billingCity', undefined);
    form.setFieldValue('billingCityId', undefined);

    const billingCountryId = (
      option as {
        lable: number;
        value: string;
      }
    )?.lable as number;
    form.setFieldValue('billingCountryId', billingCountryId);

    if (form.getFieldValue('billingSameAsSipping') === false) {
      await tempCartUsersHandle('billingCountryId', billingCountryId);
    }
  };

  const onStateChange = async (
    _: any,
    option?:
      | {
        lable: number;
        value: string;
      }
      | {
        lable: number;
        value: string;
      }[]
      | undefined,
  ) => {
    // Reset dependent fields — the useEffect watcher on stateId will fetch cities automatically
    setCities([]);
    form.setFieldValue('city', undefined);
    form.setFieldValue('cityId', undefined);

    const stateId = (
      option as {
        lable: number;
        value: string;
      }
    )?.lable as number;
    form.setFieldValue('stateId', stateId);
    await tempCartUsersHandle('stateId', stateId);
  };

  const onBillingStateChange = async (
    _: any,
    option?:
      | {
        lable: number;
        value: string;
      }
      | {
        lable: number;
        value: string;
      }[]
      | undefined,
  ) => {
    // Reset dependent fields — the useEffect watcher on billingStateId will fetch cities automatically
    setBillingCities([]);
    form.setFieldValue('billingCity', undefined);
    form.setFieldValue('billingCityId', undefined);

    const billingStateId = (
      option as {
        lable: number;
        value: string;
      }
    )?.lable as number;
    form.setFieldValue('billingStateId', billingStateId);

    if (form.getFieldValue('billingSameAsSipping') === false) {
      await tempCartUsersHandle('billingStateId', billingStateId);
    }
  };

  const onCityChange = async (
    _: any,
    option?:
      | {
        lable: number;
        value: string;
      }
      | {
        lable: number;
        value: string;
      }[]
      | undefined,
  ) => {
    const cityId = (
      option as {
        lable: number;
        value: string;
      }
    )?.lable as number;

    form.setFieldValue('cityId', cityId);
    await tempCartUsersHandle('cityId', cityId);
  };

  const onBillingCityChange = async (
    _: any,
    option?:
      | {
        lable: number;
        value: string;
      }
      | {
        lable: number;
        value: string;
      }[]
      | undefined,
  ) => {
    const billingCityId = (
      option as {
        lable: number;
        value: string;
      }
    )?.lable as number;

    form.setFieldValue('billingCityId', billingCityId);
    if (form.getFieldValue('billingSameAsSipping') === false) {
      await tempCartUsersHandle('billingCityId', billingCityId);
    }
  };

  const getCartDetailsHandle = async (cartId?: number, localUserIdGlobalState?: string) => {
    try {
      const tempUserId = localUserIdGlobalState ? localUserIdGlobalState : localStorage.getItem('userId');
      if (!tempUserId) {
        throw new Error('localStorage.getItem(`userId`) not exists');
      }
      const result = await getCartDetails(tempUserId.toString(), cartId);
      setCart(result);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    }
  };

  const getCartSummaryHandle = async (cartId?: number) => {
    try {
      setCartSummaryLoading(true)
      const result = await getCartSummary(cartId);
      setCartSummary(result);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setCartSummaryLoading(false)
    }
  };

  const addCouponCodeHandle = async (coupon_code: string, cartId?: number) => {
    try {
      await addCouponCode(coupon_code, cartId);
      return true;
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
      return false;
    }
  };

  const fireConfettiOnButton = (buttonId: string) => {
    const button = document.getElementById(buttonId);
    const canvas = document.getElementById(
      'coupon-confetti',
    ) as HTMLCanvasElement | null;

    if (button && canvas) {
      const rect = button.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();

      const x =
        (rect.left + rect.width / 2 - canvasRect.left) / canvasRect.width;
      const y =
        (rect.top + rect.height / 2 - canvasRect.top) / canvasRect.height;

      const myConfetti = confetti.create(canvas, {
        resize: true,
        useWorker: true,
      });

      myConfetti({
        particleCount: 50,
        spread: 60,
        startVelocity: 25,
        origin: { x, y },
        colors: ['#ffffff', '#FFD700', '#FF69B4', '#87CEEB', '#32CD32'],
      });
    }
  };

  const applyCouponHandle = async () => {
    setcCouponLoading(true);
    const success = await addCouponCodeHandle(coupon, cart?.cart_id);
    if (success) {
      fireConfettiOnButton('checkout-v2-coupon-btn');
    }
    await getCartSummaryHandle(cart?.cart_id);
    setCartSummaryLoading(false)
    setcCouponLoading(false);
  };

  const removeCouponHandle = async () => {
    try {
      setLoadingRemoveCoupon(true);
      const couponCode = coupon || cartSummary?.coupon_code?.[0]?.coupon_code;
      await handleRemoveCoupon(couponCode, cart?.cart_id);
      await getCartSummaryHandle(cart?.cart_id);
      setCartSummaryLoading(false)
      setCoupon('')
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoadingRemoveCoupon(false);
    }
  };

  const checkEmailExistHandle = async (email: string) => {
    try {
      await checkEmailExist(email);
      return true;
    } catch (error) {
      return false;
    }
  };

  const onEmailBlurHandle = async () => {
    const email = form.getFieldValue('email');
    // Save email to temp cart user on blur (regardless of modal state)
    await tempCartUsersHandle('email', email);
  };

  // Prefill password field with first 4 letters of email and current date (DDMM format)
  useEffect(() => {
    if (isAuthenticated) return;
    const email = watchedEmail?.trim() ?? '';
    if (!email) {
      const currentPassword = form.getFieldValue('password');
      if (currentPassword === lastPrefilledPasswordRef.current) {
        form.setFieldsValue({ password: '' });
        lastPrefilledPasswordRef.current = '';
        setPasswordStrength(0);
      }
      return;
    }

    const firstPart = email.slice(0, 4);
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const newPrefill = `${firstPart}${day}${month}`;

    const currentPassword = form.getFieldValue('password');
    if (!currentPassword || currentPassword === lastPrefilledPasswordRef.current) {
      form.setFieldsValue({ password: newPrefill });
      lastPrefilledPasswordRef.current = newPrefill;
      setPasswordStrength(getPasswordStrength(newPrefill));
    }
  }, [watchedEmail, isAuthenticated, form]);

  // ─── Reactively check email existence whenever the email field changes ────────
  // This handles both manual typing AND programmatic prefill from local storage /
  // temp-cart-user API on page load. A 700ms debounce prevents firing on every
  // keystroke; the effect is skipped when the user is already authenticated.
  useEffect(() => {
    if (isAuthenticated) {
      setEmailAlreadyExists(false);
      return;
    }

    const email = watchedEmail?.trim() ?? '';

    // Basic email format check before hitting the API
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValidEmail) {
      setEmailAlreadyExists(false);
      return;
    }

    const timer = setTimeout(async () => {
      const result = await checkEmailExistHandle(email);
      if (result) {
        setEmailAlreadyExists(true);
        setAuthModal((prevState) => ({
          ...prevState,
          login: true,
          email,
          emailExistsMessage: true,
        }));
      } else {
        setEmailAlreadyExists(false);
      }
    }, 700);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedEmail, isAuthenticated]);

  const defaultCountry = useMemo(() => ({
    name: countries.find(i => i.code?.toUpperCase() == countryState?.selectedCountry?.code?.toUpperCase())?.name || 'United States',
    id: countries.find(i => i.code?.toUpperCase() == countryState?.selectedCountry?.code?.toUpperCase())?.id || 231,
  }), [countries, countryState?.selectedCountry?.code]);

  // When defaultCountry changes (e.g. countryState or countries list loaded), sync the form field.
  // The useEffect watchers on watchedCountryId / watchedBillingCountryId will then automatically fetch states.
  // Skip overriding countryId when the user is actively editing an address — the edit handler
  // already seeds the correct country and the user may have just changed it manually.
  useEffect(() => {
    if (addressModal.currentClickType === 'edit' || billingAddressModal.currentClickType === 'edit') {
      return;
    }
    if (countries.length === 0) {
      return;
    }
    if (hasSyncedDefaultCountryRef.current) {
      return;
    }
    form.setFieldsValue({
      country: defaultCountry.name,
      countryId: defaultCountry.id,
      billingCountry: defaultCountry.name,
      billingCountryId: defaultCountry.id,
    });
    hasSyncedDefaultCountryRef.current = true;
  }, [defaultCountry, countries, addressModal.currentClickType, billingAddressModal.currentClickType]);

  // ─── Reactive cascade: shipping country → states ─────────────────────────────
  useEffect(() => {
    if (!watchedCountryId) return;
    let cancelled = false;
    setGetStatesLoading(true);
    getStatesHandle(watchedCountryId).then((result) => {
      if (cancelled) return;
      setGetStatesLoading(false);
      if (result) setStates(result);
    });
    return () => { cancelled = true; };
  }, [watchedCountryId]);

  // ─── Mirror shipping country → billing country (always in sync, billing is disabled) ───
  useEffect(() => {
    if (!watchedCountryId) return;
    const matchedCountry = countries.find((c) => c.id === watchedCountryId);
    if (!matchedCountry) return;
    form.setFieldsValue({
      billingCountry: matchedCountry.name,
      billingCountryId: matchedCountry.id,
    });
  }, [watchedCountryId, countries]);

  // ─── Reactive cascade: shipping state → cities ───────────────────────────────
  useEffect(() => {
    if (!watchedStateId) return;
    let cancelled = false;
    setGetCitiesLoading(true);
    getCitiesHandle(watchedStateId).then((result) => {
      if (cancelled) return;
      setGetCitiesLoading(false);
      if (result) setCities(result);
    });
    return () => { cancelled = true; };
  }, [watchedStateId]);

  // ─── Reactive cascade: billing country → billing states ──────────────────────
  useEffect(() => {
    if (!watchedBillingCountryId) return;
    let cancelled = false;
    setGetBillingStatesLoading(true);
    getStatesHandle(watchedBillingCountryId).then((result) => {
      if (cancelled) return;
      setGetBillingStatesLoading(false);
      if (result) setBillingStates(result);
    });
    return () => { cancelled = true; };
  }, [watchedBillingCountryId]);

  // ─── Reactive cascade: billing state → billing cities ────────────────────────
  useEffect(() => {
    if (!watchedBillingStateId) return;
    let cancelled = false;
    setGetBillingCitiesLoading(true);
    getCitiesHandle(watchedBillingStateId).then((result) => {
      if (cancelled) return;
      setGetBillingCitiesLoading(false);
      if (result) setBillingCities(result);
    });
    return () => { cancelled = true; };
  }, [watchedBillingStateId]);

  const tempCartUsersHandle = async (
    field: any,
    value: any,
  ): Promise<void> => {
    try {
      await tempCartUsers({
        cart_id: cart?.cart_id,
        fieldName: field,
        fieldValue: value,
        zl_uid: userData?.zl_uid,
      });
    } catch (error) {
      console.error(`Failed to update address field "${field}":`, error);
    }
  };

  /**
   * Generic field blur handler — reads the current value from the form
   * and persists it to the backend temp-cart-user record.
   *
   * Guards:
   * - Billing fields are only saved when billingSameAsSipping === false
   * - gstNumber is only saved when isGst === true
   */
  const onFieldBlurHandle = async (fieldName: string) => {
    const value = form.getFieldValue(fieldName);
    if (value === undefined || value === null || value === '') return;

    // Billing address fields: only save when billing differs from shipping
    const billingFields = [
      'billingAddress', 'billingCountry', 'billingCountryId',
      'billingState', 'billingStateId', 'billingCity', 'billingCityId', 'billingPincode',
    ];
    if (billingFields.includes(fieldName) && form.getFieldValue('billingSameAsSipping') !== false) {
      return;
    }

    // GST number: only save when the user has opted in to GST details
    if (fieldName === 'gstNumber' && !form.getFieldValue('isGst')) {
      return;
    }

    await tempCartUsersHandle(fieldName, value);
  };

  /**
   * Fetches previously saved form data from the temp-cart-user API
   * and pre-fills the checkout form on page load / refresh.
   */
  const prefillFormFromTempCartUser = async (cartId: number) => {
    try {
      const response = await getTempCartUser(cartId);
      const savedData = response?.data;
      if (!savedData) return;

      // Seed the local `others` cache from the server's current state
      // so any subsequent blur-saves merge against the full picture.
      syncOthersCache(cartId, savedData.others);

      const formValues: Record<string, any> = {};

      // Map direct backend fields back to their form field names
      if (savedData.name) formValues.fullname = savedData.name;
      if (savedData.phone) formValues.phone = savedData.phone;
      if (savedData.email) formValues.email = savedData.email;
      if (savedData.address) formValues.address = savedData.address;
      if (savedData.postal_code) formValues.pincode = savedData.postal_code;
      // if (savedData.country_id) formValues.countryId = savedData.country_id;
      // if (savedData.state_id) formValues.stateId = savedData.state_id;
      // if (savedData.city_id) formValues.cityId = savedData.city_id;
      if (savedData.country_code)
        formValues.phoneCountryCode = savedData.country_code;
      // iso2 takes priority — overrides country_code if both are present
      if (savedData.iso2) formValues.phoneCountryCode = savedData.iso2;

      // Merge extra fields stored in the `others` JSON blob
      // if (savedData.others) {
      //   try {
      //     const othersData =
      //       typeof savedData.others === 'string'
      //         ? JSON.parse(savedData.others)
      //         : savedData.others;
      //     Object.assign(formValues, othersData);
      //   } catch {
      //     // ignore JSON parse errors silently
      //   }
      // }

      form.setFieldsValue(formValues);

      // If billing differs from shipping, reveal the billing address form section
      if (formValues.billingSameAsSipping === false) {
        setBillingAddressModal((prev) => ({
          ...prev,
          currentClickType: prev.currentClickType ?? 'create',
        }));
      }

    } catch (error) {
      console.error('Failed to prefill form from temp cart user:', error);
    }
  };

  const showShippingAddressForm = !address || addressModal.currentClickType === 'create' || addressModal.currentClickType === 'edit';

  const globalCountryChangeHandle = async () => {
    try {
      setCartDetailsLoading(true);
      await getCartDetailsHandle();
      setCartDetailsLoading(false);
    } catch (error) {
      console.error(error)
    }
  }

  // const handleCurrentLocationClick = async () => {
  //   if (typeof window === 'undefined' || !navigator.geolocation) {
  //     notify('Geolocation is not supported by your browser.', 'error');
  //     return;
  //   }

  //   try {
  //     setDetectingLocation(true);
  //     const position = await new Promise<GeolocationPosition>((resolve, reject) => {
  //       navigator.geolocation.getCurrentPosition(resolve, reject, {
  //         enableHighAccuracy: false,
  //         timeout: 10000,
  //       });
  //     });

  //     const { latitude, longitude } = position.coords;
  //     let countryName = '';
  //     let stateName = '';
  //     let cityName = '';
  //     let pincode = '';
  //     let formattedAddress = '';

  //     const service = process.env.NEXT_PUBLIC_LOCAITON_SERVICE || process.env.NEXT_PUBLIC_LOCATION_SERVICE;

  //     if (service === 'google') {
  //       const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';
  //       if (!apiKey) {
  //         notify('Google Maps API key is not configured.', 'error');
  //         setDetectingLocation(false);
  //         return;
  //       }

  //       const response = await fetch(
  //         `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
  //       );
  //       const data = await response.json();

  //       if (data.status !== 'OK' || !data.results || data.results.length === 0) {
  //         notify(data.error_message || 'Unable to retrieve location details from Google API.', 'error');
  //         setDetectingLocation(false);
  //         return;
  //       }

  //       const addressComponents = data.results[0].address_components;
  //       const excludedTypes = [
  //         'locality',
  //         'administrative_area_level_2',
  //         'administrative_area_level_1',
  //         'country',
  //         'postal_code',
  //       ];
  //       const localParts = addressComponents
  //         .filter((component: any) =>
  //           !component.types.some((type: string) => excludedTypes.includes(type))
  //         )
  //         .map((component: any) => component.long_name);
  //       formattedAddress = localParts.length > 0 ? localParts.join(', ') : (data.results[0].formatted_address || '');

  //       for (const component of addressComponents) {
  //         const types = component.types;
  //         if (types.includes('country')) {
  //           countryName = component.long_name;
  //         } else if (types.includes('administrative_area_level_1')) {
  //           stateName = component.long_name;
  //         } else if (types.includes('locality')) {
  //           cityName = component.long_name;
  //         } else if (types.includes('postal_code')) {
  //           pincode = component.long_name;
  //         } else if (!cityName && types.includes('administrative_area_level_2')) {
  //           cityName = component.long_name;
  //         }
  //       }
  //     } else {
  //       // Using OpenStreetMap Nominatim (open-source, no API key required)
  //       const response = await fetch(
  //         `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
  //         { headers: { 'Accept-Language': 'en' } }
  //       );
  //       const data = await response.json();

  //       if (!data || data.error || !data.address) {
  //         notify(data.error || 'Unable to retrieve location details.', 'error');
  //         setDetectingLocation(false);
  //         return;
  //       }

  //       const address = data.address;
  //       countryName = address.country || '';
  //       stateName = address.state || address.region || '';
  //       cityName = address.city || address.town || address.village || address.state_district || address.county || '';
  //       pincode = address.postcode || '';

  //       const road = address.road || '';
  //       const neighbourhood = address.neighbourhood || address.suburb || address.quarter || '';
  //       const county = address.county || '';
  //       const building = address.building || address.house_number || address.amenity || '';
  //       const addrParts = [building, road, neighbourhood, county].filter(Boolean);
  //       formattedAddress = addrParts.join(', ') || data.display_name || '';
  //     }

  //     if (!countryName && !stateName && !cityName && !pincode) {
  //       notify('Could not retrieve location details.', 'warning');
  //       setDetectingLocation(false);
  //       return;
  //     }

  //     // If form is not shown, switch to create address view
  //     if (!showShippingAddressForm) {
  //       setAddressModal((prev) => ({
  //         ...prev,
  //         currentClickType: 'create',
  //       }));
  //     }

  //     // Find country match
  //     const matchedCountry = countries.find(
  //       (c) => c.name.toLowerCase() === countryName.toLowerCase()
  //     );

  //     if (matchedCountry) {
  //       form.setFieldValue('country', matchedCountry.name);
  //       form.setFieldValue('countryId', matchedCountry.id);

  //       const selectedCounty = countryList?.find((c) => c?.code === matchedCountry.code);
  //       const selectedGlobalCountryCode = selectedCounty?.code || 'US';

  //       await globalCountryChange({
  //         cartData: formatCartData(
  //           cart?.data?.[0]?.cart_items,
  //           cart?.currency_code,
  //           cart?.cart_id
  //         ),
  //         code: selectedGlobalCountryCode,
  //         countryList,
  //         fallbackCountry,
  //         localUserId,
  //         router,
  //         setCountryState,
  //         source: 'manual',
  //         userData,
  //       });
  //       await globalCountryChangeHandle();
  //       await tempCartUsersHandle('countryId', matchedCountry.id);

  //       // Fetch and match states
  //       const statesList = await getStatesHandle(matchedCountry.id);
  //       if (statesList) {
  //         setStates(statesList);
  //         const matchedState = statesList.find(
  //           (s: any) =>
  //             s.name.toLowerCase() === stateName.toLowerCase() ||
  //             s.name.toLowerCase().includes(stateName.toLowerCase()) ||
  //             stateName.toLowerCase().includes(s.name.toLowerCase())
  //         );

  //         if (matchedState) {
  //           form.setFieldValue('state', matchedState.name);
  //           form.setFieldValue('stateId', matchedState.id);
  //           await tempCartUsersHandle('stateId', matchedState.id);

  //           // Fetch and match cities
  //           const citiesList = await getCitiesHandle(matchedState.id);
  //           if (citiesList) {
  //             setCities(citiesList);
  //             const matchedCity = citiesList.find(
  //               (c: any) =>
  //                 c.name.toLowerCase() === cityName.toLowerCase() ||
  //                 c.name.toLowerCase().includes(cityName.toLowerCase()) ||
  //                 cityName.toLowerCase().includes(c.name.toLowerCase())
  //             );

  //             if (matchedCity) {
  //               form.setFieldValue('city', matchedCity.name);
  //               form.setFieldValue('cityId', matchedCity.id);
  //               await tempCartUsersHandle('cityId', matchedCity.id);
  //             } else {
  //               form.setFieldValue('city', undefined);
  //               form.setFieldValue('cityId', undefined);
  //             }
  //           }
  //         } else {
  //           form.setFieldValue('state', undefined);
  //           form.setFieldValue('stateId', undefined);
  //           form.setFieldValue('city', undefined);
  //           form.setFieldValue('cityId', undefined);
  //           setCities([]);
  //         }
  //       }
  //     } else {
  //       notify(`Country "${countryName}" is not supported.`, 'warning');
  //     }

  //     if (pincode) {
  //       form.setFieldValue('pincode', pincode);
  //       await tempCartUsersHandle('postal_code', pincode);
  //     }

  //     if (formattedAddress) {
  //       form.setFieldValue('address', formattedAddress);
  //       await tempCartUsersHandle('address', formattedAddress);
  //     }

  //   } catch (error: any) {
  //     console.error(error);
  //     if (typeof window !== 'undefined' && error instanceof GeolocationPositionError) {
  //       switch (error.code) {
  //         case GeolocationPositionError.PERMISSION_DENIED:
  //           notify('Location access denied. Please enable location permission.', 'error');
  //           break;
  //         case GeolocationPositionError.POSITION_UNAVAILABLE:
  //           notify('Position unavailable. Check your connection or GPS.', 'error');
  //           break;
  //         case GeolocationPositionError.TIMEOUT:
  //           notify('Geolocation request timed out.', 'error');
  //           break;
  //         default:
  //           notify('Failed to detect current location.', 'error');
  //       }
  //     } else {
  //       notify('Failed to resolve current location.', 'error');
  //     }
  //   } finally {
  //     setDetectingLocation(false);
  //   }
  // };

  const initFunction = async () => {
    setGetCountriesLoading(true);
    setGetBillingCountriesLoading(true);
    const countries = await getAllCountriesHandle();
    setGetCountriesLoading(false);
    setGetBillingCountriesLoading(false);
    if (countries) {
      setCountries(countries);
      setBillingCountries(countries);
    }

    form.setFieldsValue({
      country: defaultCountry.name,
      billingCountry: defaultCountry.name,
      countryId: defaultCountry.id,
      billingCountryId: defaultCountry.id
    });
    // States are fetched reactively via the watchedCountryId useEffect
  };




  const getCartSummaryOnCartsApiCall = async () => {
    try {
      if (cart?.cart_id) {
        await getCartSummaryHandle(cart?.cart_id);
        await prefillFormFromTempCartUser(cart?.cart_id);
        const url = new URL(window.location.href);
        url.searchParams.set('cartId', String(cart.cart_id));
        window.history.replaceState(null, '', url.toString());
      }
    } catch (error) {
      console.error(error)
    } finally {
      setCartSummaryLoading(false)
    }
  }

  const onAuthenticationHandle = async () => {
    if (isAuthenticated) {
      setLoadingAddress(true);
      await getAddressesHandle();
      setLoadingAddress(false);
      setCartDetailsLoading(true);
      await getCartDetailsHandle(parseInt((cartId as string)));
      setCartDetailsLoading(false);
    }
  }

  const fetchCountries = async () => {
    try {
      const data: CountryData[] = await getActiveCountries();
      setCountryList(
        data?.map((data) => ({ ...data, phoneCode: data.phonecode || '' })) ||
        [],
      );
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  useEffect(() => {
    checkIsAuthenticated();
  }, [])

  useEffect(() => {
    initFunction();
  }, []);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (localUserId) {
      setCartDetailsLoading(true);
      getCartDetailsHandle(parseInt((cartId as string)), localUserId);
      setCartDetailsLoading(false);
    }
  }, [localUserId])

  useEffect(() => {
    getCartSummaryOnCartsApiCall()
  }, [cart]);

  useEffect(() => {
    onAuthenticationHandle()
  }, [isAuthenticated])

  useEffect(() => {
    if (addresses?.length) {
      // Prefer the address marked as default; fall back to the first one
      const adrs: any =
        addresses.find((a: any) => a.set_default === 1) ?? addresses[0];
      setAddress(adrs);
      form.setFieldsValue({
        countryId: adrs?.country_id,
        stateId: adrs?.state_id,
        cityId: adrs?.city_id,
      });
    }
  }, [addresses]);

  useEffect(() => {
    if (userData) {
      setIsAuthenticated(true)
    }
    if (userData?.name) {
      form.setFieldsValue({
        fullname: userData?.name || '',
      });
    }
    if (userData?.phone) {
      form.setFieldsValue({
        phone: userData?.phone,
        phoneCountryCode: 'in',
      });
    }
  }, [userData]);

  useEffect(() => {

    if (cancelPaymentModal) {
      const eventParams: EventDetails = {
        event_name: 'screen_view',
        source: 'global',
        source_type: 'retry_popup',
        source_id: retryOrderData?.data?.id?.toString() as string,
        sub_source: null,
        sub_source_id: null,
        unit: 'boolean',
        value: 1,
        meta: {
          click_source: 'retry_popup',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
    }

  }, [cancelPaymentModal])

  useEffect(() => {
    if (!paymentProcessing) return;

    // 1. Tab close / page refresh — only block when payment is still running
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!paymentProcessingRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };

    // 2. Next.js client-side navigation (router.push, <Link>, etc.)
    //    Use the ref so a synchronous setRef(false) before router.replace() is respected.
    const handleRouteChangeStart = () => {
      if (!paymentProcessingRef.current) return; // payment done — allow navigation
      const confirmed = window.confirm(
        'Payment is being processed. Are you sure you want to leave?',
      );
      if (!confirmed) {
        // Emit routeChangeError to abort the navigation
        router.events.emit('routeChangeError');
        // This throw is intentional — it is the standard Next.js way to cancel a route change
        throw 'Route change aborted: payment in progress.';
      }
    };

    // 3. Browser back / forward buttons
    router.beforePopState(() => {
      if (!paymentProcessingRef.current) return true; // payment done — allow
      const confirmed = window.confirm(
        'Payment is being processed. Are you sure you want to leave?',
      );
      if (!confirmed) {
        // Push current URL back to undo the popstate
        router.push(router.asPath);
        return false;
      }
      return true;
    });

    window.addEventListener('beforeunload', handleBeforeUnload);
    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      router.events.off('routeChangeStart', handleRouteChangeStart);
      // Reset beforePopState so it doesn't block navigation after payment completes
      router.beforePopState(() => true);
    };
  }, [paymentProcessing]);

  const totalDiscount =
    Number(cartSummary?.product_regular_price) -
    parseFloat(cartSummary?.sub_total?.replaceAll(',', '')) +
    parseFloat(cartSummary?.discount?.replaceAll(',', ''));

  const selectStyles = {
    popup: {
      root: { fontFamily: montserrat.style.fontFamily },
    },
  };

  const steps = (
    <Steps
      prefixCls='checkout'
      current={2}
      percent={0}
      labelPlacement='vertical'
      items={items}
      size='small'
    />
  );

  const isDigitalProduct: boolean = cart?.data?.[0]?.cart_items?.some(
    (item: any) => item.book_type === 'digital',
  );


  const inputClassName = 'placeholder:text-grayZl-700';
  const inputParentClassName = 'flex lg:flex-row flex-col gap-4';
  const formItemClassName = 'flex-1 !m-0';
  const inputSize = 'large';
  const inputVarient = 'outlined';
  // const cardClass = ' p-4 bg-grayZl-950 rounded-[12px]';
  // const cardTitleClass = ' text-base text-white font-medium mb-4';
  const formItemPrefixCls = 'checkout-v2';
  const cardChildClassName = ' space-y-4';
  const childCardClassName = 'bg-grayZl-925 rounded-xl p-4 gap-4';
  const priceClassName = 'text-sm font-semibold';

  // Temporary workaround to bypass unused variable errors from commented-out billing/GST code
  if (false as boolean) {
    console.info(
      Checkbox,
      getBillingCountriesLoading,
      getBillingStatesLoading,
      getBillingCitiesLoading,
      billingCountries,
      billingStates,
      billingCities,
      isGst,
      onBillingCountryChange,
      onBillingStateChange,
      onBillingCityChange
    );
  }

  return (
    <div className='p-4 pb-24 md:pb-4'>
      <div className=' max-w-7xl mx-auto'>
        <Form
          form={form}
          name='control-hooks'
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          style={{ maxWidth: 600 }}
          className='max-w-full '
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        >
          <AnimatePresence>
            <div className='flex  lg:flex-row flex-col flex-1 gap-4'>
              <motion.div
                key='checkout-left'
                className='flex flex-col gap-4 flex-1'
                initial={{ opacity: 0, x: -30, filter: 'blur(4px)' }}
                animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <RenderIf condition={!twScreen.lg && !idBuyNow}>
                  <div className='flex items-start'>
                    <div className='flex items-center gap-1' onClick={() => router.back()}>
                      <ChevronLeftIcon className='w-4 h-4' /><p className='text-white text-xs'>Back</p>
                    </div>
                  </div>
                </RenderIf>
                <RenderIf condition={idBuyNow && !twScreen.sm && cart?.data?.[0]?.cart_items?.[0]?.product_name}>
                  <CheckoutV2Card >
                    <div>
                      <div className='flex items-start  gap-4'>
                        <div className='flex items-start  gap-4'>
                          <div className='min-w-[90px] w-[90px] min-h-[90px] max-h-[90px] h-[90px]  p-1 flex items-center justify-center'>
                            <Image
                              className='w-auto h-full bg-contain'
                              src={cart?.data?.[0]?.cart_items?.[0]?.product_thumbnail_image || ''}
                              // src={BookDummy}
                              alt='Book Image'
                              width={100}
                              height={100}
                            />
                          </div>
                          <div>
                            <p className='text-sm text-grayZl-600'>
                              Qty : {cart?.data?.[0]?.cart_items?.[0]?.quantity}
                            </p>
                            <p className=''>{cart?.data?.[0]?.cart_items?.[0]?.product_name}</p>
                          </div>
                        </div>
                        <div>
                          <p className={twMerge('text-nowrap ', priceClassName)}>
                            {cart?.data?.[0]?.cart_items?.[0]?.currency_symbol + ' ' + cart?.data?.[0]?.cart_items?.[0]?.price + ' /-'}
                          </p>
                          <p
                            className={twMerge(
                              'text-nowrap line-through text-grayZl-600 text-sm font-semibold',
                              priceClassName,
                            )}
                          >
                            {cart?.data?.[0]?.cart_items?.[0]?.currency_symbol + ' ' + cart?.data?.[0]?.cart_items?.[0]?.stroked_price + ' /-'}
                          </p>
                        </div>
                      </div>
                      <div className='flex items-center justify-between  mt-1'>
                        <p className='text-xs font-semibold text-grayZl-700'>{cart?.data?.[0]?.cart_items?.[0]?.category}</p>
                        <p
                          className='text-xs text-primary font-medium underline cursor-pointer'
                          onClick={() => {
                            const el = document.getElementById('checkout-v2-coupon-input');
                            if (el) {
                              const top = el.getBoundingClientRect().top + window.scrollY - 120;
                              window.scrollTo({ top, behavior: 'smooth' });
                              setTimeout(() => el.focus(), 400);
                            }
                          }}
                        >Have a Coupon code?</p>
                      </div>
                    </div>

                  </CheckoutV2Card>
                </RenderIf>
                <RenderIf condition={idBuyNow && !twScreen.sm && cartDetailsLoading}>
                  <CheckoutV2Card >
                    <Skeleton />
                  </CheckoutV2Card>
                </RenderIf>
                {/* <CheckoutV2Card className='p-0'>
                <RenderIf condition={!twScreen.lg}>
                  <div className='p-4'>{steps}</div>
                </RenderIf>
              </CheckoutV2Card> */}
                <CheckoutV2Card title='Your information'>
                  <>
                    <div className={twMerge('', inputParentClassName)}>
                      <RenderIf condition={!isAuthenticated}>
                        <div className='flex-1' id='form-anchor-email'>
                          <Form.Item
                            prefixCls={formItemPrefixCls}
                            className={twMerge('', formItemClassName)}
                            layout='vertical'
                            name='email'
                            // // label='Email Address'
                            rules={[{ required: true }]}
                          >
                            <Input
                              className={inputClassName}
                              size={inputSize}
                              variant={inputVarient}
                              onBlur={onEmailBlurHandle}
                              placeholder='Email Address'
                            />
                          </Form.Item>
                          <RenderIf condition={emailAlreadyExists}>
                            <p
                              className='text-red-500 text-xs mt-1 font-medium cursor-pointer'
                              onClick={() =>
                                setAuthModal((prevState) => ({
                                  ...prevState,
                                  login: true,
                                  email: watchedEmail?.trim(),
                                  emailExistsMessage: true,
                                }))
                              }
                            >
                              Email already exists,{' '}
                              <span className='underline font-semibold'>
                                please login
                              </span>
                              .
                            </p>
                          </RenderIf>
                        </div>
                      </RenderIf>
                      <div className='flex-1' id='form-anchor-fullname'>
                        <Form.Item
                          prefixCls={formItemPrefixCls}
                          className={twMerge('flex-1', formItemClassName)}
                          layout='vertical'
                          name='fullname'
                          // // label='Full Name'
                          rules={[{ required: true }]}
                        >
                          <Input
                            className={inputClassName}
                            size={inputSize}
                            variant={inputVarient}
                            placeholder='Full Name'
                            onBlur={() => onFieldBlurHandle('fullname')}
                          />
                        </Form.Item>
                      </div>

                    </div>
                    <div className={twMerge('', inputParentClassName)}>
                      <div className='flex-1' id='form-anchor-phone'>
                        <Form.Item
                          prefixCls={formItemPrefixCls}
                          style={{
                            marginBottom: 0,
                          }}
                          className={twMerge('flex-1', formItemClassName)}
                          layout='vertical'
                          name='phone'
                          // // label='Phone Number'
                          rules={[
                            {
                              validator: (_, value) => {
                                if (!value || value.trim() === '') {
                                  return Promise.reject(
                                    new Error('Please input your phone number!'),
                                  );
                                }
                                if (!phoneValidRef.current) {
                                  return Promise.reject(
                                    new Error('Please enter a valid phone number'),
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <IntlTelInput
                            placeholder='Phone Number'
                            autoPlaceholder={false}
                            defaultCountry={
                              cartSummary?.country_code?.toLowerCase() || 'in'
                            }
                            containerClassName='flex intl-tel-input z-10 w-full ant-input ant-input-lg ant-input-outlined checkout-intl-tel-input'
                            inputClassName={
                              'flex-1 bg-transparent outline-none ant-input ant-input-lg   ant-input-status-success ps-14' +
                              ' ' +
                              inputClassName
                            }
                            onSelectFlag={(_number, countryData) => {
                              form.setFieldsValue({
                                phoneCountryCode:
                                  countryData.iso2?.toLowerCase() || 'in',
                                phonePrefix: countryData.dialCode || '91',
                              });
                              form.validateFields(['phone']);
                            }}
                            onPhoneNumberChange={(isValid, value, countryData) => {
                              phoneValidRef.current = isValid;
                              form.setFieldsValue({
                                phone: value,
                                phoneCountryCode:
                                  countryData.iso2?.toLowerCase() || 'in',
                                phonePrefix: countryData.dialCode || '91',
                              });
                              form.validateFields(['phone']);
                            }}
                            onPhoneNumberBlur={() => {
                              onFieldBlurHandle('phone');
                              onFieldBlurHandle('phoneCountryCode');
                            }}
                          />
                        </Form.Item>
                      </div>
                      <RenderIf condition={!isAuthenticated}>
                        <div className={twMerge('', formItemClassName)}>
                          <Form.Item
                            prefixCls={formItemPrefixCls}
                            className='!m-0'
                            layout='vertical'
                            name='password'
                            // // label='Create Account Password'
                            rules={[
                              { required: true },
                              { min: 8, message: 'Password must be at least 8 characters' },
                            ]}
                          >
                            <Input.Password
                              classNames={{
                                input: 'placeholder:text-grayZl-700'
                              }}
                              className={inputClassName}
                              size={inputSize}
                              variant={inputVarient}
                              placeholder='Create Account Password'
                              onChange={(e) =>
                                setPasswordStrength(getPasswordStrength(e.target.value))
                              }
                            />
                          </Form.Item>
                          <RenderIf condition={passwordStrength > 0}>
                            <div style={{ marginTop: 8 }}>
                              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                                {[1, 2, 3, 4].map((bar) => (
                                  <div
                                    key={bar}
                                    style={{
                                      flex: 1,
                                      height: 4,
                                      borderRadius: 4,
                                      backgroundColor:
                                        bar <= passwordStrength
                                          ? passwordStrengthConfig[passwordStrength]?.color
                                          : '#374151',
                                      transition: 'background-color 0.3s ease',
                                    }}
                                  />
                                ))}
                              </div>
                              <p
                                style={{
                                  fontSize: 12,
                                  color: passwordStrengthConfig[passwordStrength]?.color,
                                  margin: 0,
                                  fontWeight: 500,
                                }}
                              >
                                {passwordStrengthConfig[passwordStrength]?.label}
                              </p>
                            </div>
                          </RenderIf>
                        </div>
                      </RenderIf>
                    </div>
                  </>
                </CheckoutV2Card>

                <CheckoutV2Card title='Shipping Address'>
                  <>
                    <div className='flex justify-end gap-2'>
                      {/* <RenderIf condition={(addressModal.currentClickType === 'create' || addresses?.length === 0) && !loadingAddress}>
                        <button
                          type='button'
                          onClick={handleCurrentLocationClick}
                          disabled={detectingLocation}
                          className='text-white flex items-center gap-1 disabled:opacity-50 bg-grayZl-850 px-2 py-1 rounded'
                        >
                          {detectingLocation ? (
                            <LoadingOutlined className='animate-spin' />
                          ) : (
                            <MdOutlineMyLocation />
                          )}
                          {detectingLocation ? 'Detecting...' : 'Current Location'}
                        </button>
                      </RenderIf> */}
                      <RenderIf condition={Boolean(addresses?.length)}>

                        <button
                          onClick={() =>
                            setAddressModal((prev) => ({ ...prev, show: true }))
                          }
                          type='button'
                          className='self-end text-primary font-medium font-sm  px-2 py-1 rounded'
                        >
                          Change Address
                        </button>

                      </RenderIf>
                    </div>
                    <RenderIf condition={loadingAddress}>
                      <Skeleton />
                    </RenderIf>
                    <RenderIf
                      condition={
                        address &&
                        addressModal.currentClickType === null &&
                        !loadingAddress
                      }
                    >
                      <div className={twMerge('', childCardClassName)}>
                        <div className={twMerge('flex gap-2 items-start')}>
                          <Radio value={1} checked></Radio>
                          <div>
                            <p>
                              {address?.address}, {address?.postal_code},{' '}
                              {address?.city_name}, {address?.state_name},{' '}
                              {address?.country_name}
                            </p>
                          </div>
                        </div>
                      </div>
                    </RenderIf>
                    <RenderIf
                      condition={showShippingAddressForm}
                    >
                      <div id='form-anchor-address'>
                        <Form.Item
                          prefixCls={formItemPrefixCls}
                          layout='vertical'
                          name='address'
                          // // label='Type your complete address'
                          rules={[{ required: true }]}
                        >
                          <TextArea
                            size={inputSize}
                            variant={inputVarient}
                            className={inputClassName}
                            rows={4}
                            placeholder='Type your complete address'
                            onBlur={() => onFieldBlurHandle('address')}
                          />
                        </Form.Item>
                      </div>
                      <div className={cardChildClassName}>
                        <div className={twMerge('', inputParentClassName)}>
                          <div className='flex-1' id='form-anchor-country'>
                            <Form.Item
                              prefixCls={formItemPrefixCls}
                              className='flex-1'
                              layout='vertical'
                              name='country'
                              // // label='Select Country'
                              rules={[{ required: true }]}
                            >
                              <ResponsiveSelect
                                prefixCls={formItemPrefixCls}
                                size={inputSize}
                                variant={inputVarient}
                                className={inputClassName}
                                styles={selectStyles}
                                onChange={onCountryChange}
                                loading={getCountriesLoading}
                                options={countries.map((i) => ({
                                  lable: i.id,
                                  value: i.name,
                                }))}
                                placeholder='Select Country'
                              />
                            </Form.Item>
                          </div>
                          <div className='flex-1' id='form-anchor-pincode'>
                            <Form.Item
                              prefixCls={formItemPrefixCls}
                              className='flex-1'
                              layout='vertical'
                              name='pincode'
                              dependencies={['country', 'countryId']}
                              validateTrigger={['onChange', 'onBlur']}
                              rules={[
                                { required: true, message: 'Please enter pincode' },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    if (!value) {
                                      return Promise.resolve();
                                    }
                                    const isIndia = isSelectedCountryIndia(
                                      getFieldValue('country'),
                                      getFieldValue('countryId'),
                                    );
                                    if (isIndia) {
                                      const cleanVal = String(value).trim();
                                      if (!/^[1-9][0-9]{5}$/.test(cleanVal)) {
                                        return Promise.reject(
                                          new Error(
                                            'Please enter a valid 6-digit pincode',
                                          ),
                                        );
                                      }
                                    }
                                    return Promise.resolve();
                                  },
                                }),
                              ]}
                            >
                              <Input
                                className={inputClassName}
                                size={inputSize}
                                variant={inputVarient}
                                placeholder='Enter Pincode'
                                maxLength={isShippingIndia ? 6 : undefined}
                                onBlur={() => onFieldBlurHandle('pincode')}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <div className={twMerge('', inputParentClassName)}>
                          <div className='flex-1' id='form-anchor-state'>
                            <Form.Item
                              prefixCls={formItemPrefixCls}
                              className='flex-1'
                              layout='vertical'
                              name='state'
                              // label='Select State'
                              rules={[{ required: true }]}
                            >
                              <ResponsiveSelect
                                prefixCls={formItemPrefixCls}
                                size={inputSize}
                                variant={inputVarient}
                                className={inputClassName}
                                styles={selectStyles}
                                onChange={onStateChange}
                                loading={getStatesLoading}
                                options={states.map((i) => ({
                                  lable: i.id,
                                  value: i.name,
                                }))}
                                placeholder='Select State'
                              />
                            </Form.Item>
                          </div>
                          <div className='flex-1' id='form-anchor-city'>
                            <Form.Item
                              prefixCls={formItemPrefixCls}
                              className='flex-1'
                              layout='vertical'
                              name='city'
                              // label='Select City'
                              rules={[{ required: true }]}
                            >
                              <ResponsiveSelect
                                prefixCls={formItemPrefixCls}
                                size={inputSize}
                                variant={inputVarient}
                                className={inputClassName}
                                styles={selectStyles}
                                onChange={onCityChange}
                                loading={getCitiesLoading}
                                options={cities.map((i) => ({
                                  lable: i.id,
                                  value: i.name,
                                }))}
                                placeholder='Select City'
                              />
                            </Form.Item>
                          </div>
                        </div>
                      </div>
                    </RenderIf>
                  </>
                </CheckoutV2Card>
                {/* <CheckoutV2Card title='Billing address'>
                  <>
                    <Form.Item
                      prefixCls={formItemPrefixCls}
                      name='billingSameAsSipping'
                      valuePropName='checked'
                      initialValue={true}
                    >
                      <Checkbox
                        onChange={(e) => {
                          if (!e.target.checked) {
                            setBillingAddressModal((prev) => ({
                              ...prev,
                              currentClickType: 'create',
                            }));
                          }
                        }}
                      >
                        Billing address same as shipping address
                      </Checkbox>
                    </Form.Item>
                    <RenderIf
                      condition={Boolean(addresses?.length) && !billingSameAsSipping}
                    >
                      <div className='flex justify-end'>
                        <button
                          onClick={() =>
                            setBillingAddressModal((prev) => ({
                              ...prev,
                              show: true,
                            }))
                          }
                          type='button'
                          className='self-end text-primary font-medium font-sm'
                        >
                          {billingAddressModal.currentClickType === 'create'
                            ? 'Select'
                            : 'Change'}{' '}
                          Address
                        </button>
                      </div>
                    </RenderIf>
                    <RenderIf
                      condition={
                        !billingSameAsSipping &&
                        billingAddress &&
                        billingAddressModal.currentClickType === null
                      }
                    >
                      <div className={twMerge('', childCardClassName)}>
                        <div className={twMerge('flex gap-2 items-start')}>
                          <Radio value={1} checked></Radio>
                          <div>
                            <p>
                              {billingAddress?.address},{' '}
                              {billingAddress?.postal_code},{' '}
                              {billingAddress?.city_name},{' '}
                              {billingAddress?.state_name},{' '}
                              {billingAddress?.country_name}
                            </p>
                          </div>
                        </div>
                      </div>
                    </RenderIf>

                    <RenderIf
                      condition={
                        !billingSameAsSipping &&
                        billingAddressModal.currentClickType != null
                      }
                    >
                      <>
                        <Form.Item
                          prefixCls={formItemPrefixCls}
                          layout='vertical'
                          name='billingAddress'
                          // label='Type your complete address'
                          rules={[{ required: true }]}
                        >
                          <TextArea
                            rows={4}
                            placeholder='Type your complete address'
                            onBlur={() => onFieldBlurHandle('billingAddress')}
                          />
                        </Form.Item>
                        <div className={twMerge('', inputParentClassName)}>
                          <Form.Item
                            prefixCls={formItemPrefixCls}
                            className='flex-1'
                            layout='vertical'
                            name='billingCountry'
                            // // label='Select Country'
                            rules={[{ required: true }]}
                          >
                            <ResponsiveSelect
                              prefixCls={formItemPrefixCls}
                              size={inputSize}
                              variant={inputVarient}
                              className={inputClassName}
                              styles={selectStyles}
                              loading={getBillingCountriesLoading}
                              onChange={onBillingCountryChange}
                              placeholder='Select Country'
                              disabled
                              options={billingCountries.map((i) => ({
                                lable: i.id,
                                value: i.name,
                              }))}
                            />
                          </Form.Item>
                          <Form.Item
                            prefixCls={formItemPrefixCls}
                            className='flex-1'
                            layout='vertical'
                            name='billingPincode'
                            // // label='Enter Pincode'
                            rules={[{ required: true }]}
                          >
                            <Input
                              className={inputClassName}
                              size={inputSize}
                              variant={inputVarient}
                              placeholder='Enter Pincode'
                              onBlur={() => onFieldBlurHandle('billingPincode')}
                            />
                          </Form.Item>
                        </div>
                        <div className={twMerge('', inputParentClassName)}>
                          <Form.Item
                            prefixCls={formItemPrefixCls}
                            className='flex-1'
                            layout='vertical'
                            name='billingState'
                            // // label='Select State'
                            rules={[{ required: true }]}
                          >
                            <ResponsiveSelect
                              prefixCls={formItemPrefixCls}
                              size={inputSize}
                              variant={inputVarient}
                              className={inputClassName}
                              styles={selectStyles}
                              onChange={onBillingStateChange}
                              loading={getBillingStatesLoading}
                              options={billingStates.map((i) => ({
                                lable: i.id,
                                value: i.name,
                              }))}
                              placeholder='Select State'
                            />
                          </Form.Item>
                          <Form.Item
                            prefixCls={formItemPrefixCls}
                            className='flex-1'
                            layout='vertical'
                            name='billingCity'
                            // // label='Select City'
                            rules={[{ required: true }]}
                          >
                            <ResponsiveSelect
                              prefixCls={formItemPrefixCls}
                              size={inputSize}
                              variant={inputVarient}
                              className={inputClassName}
                              styles={selectStyles}
                              onChange={onBillingCityChange}
                              loading={getBillingCitiesLoading}
                              options={billingCities.map((i) => ({
                                lable: i.id,
                                value: i.name,
                              }))}
                              placeholder='Select City'
                            />
                          </Form.Item>
                        </div>
                      </>
                    </RenderIf>
                  </>
                </CheckoutV2Card> */}
                {/* <RenderIf condition={countryState.selectedCountry?.code == 'IN'}>
                  <CheckoutV2Card title='GST details'>
                    <>
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        name='isGst'
                        valuePropName='checked'
                        initialValue={false}
                      >
                        <Checkbox>Add GST details (optional)</Checkbox>
                      </Form.Item>
                      <RenderIf condition={isGst}>
                        <Form.Item
                          prefixCls={formItemPrefixCls}
                          className='flex-1'
                          layout='vertical'
                          name='gstNumber'
                          // // label='Add GST details'
                          rules={[{ required: false }]}
                        >
                          <Input
                            className={inputClassName}
                            size={inputSize}
                            variant={inputVarient}
                            placeholder='Add GST details'
                            onBlur={() => onFieldBlurHandle('gstNumber')}
                          />
                        </Form.Item>
                      </RenderIf>
                    </>
                  </CheckoutV2Card>
                </RenderIf> */}
                <CheckoutV2Card title='Payment information'>
                  <>
                    <Form.Item
                      prefixCls={formItemPrefixCls}
                      layout='vertical'
                      name='paymentOption'
                      // // label='Payment Type'
                      initialValue='online'
                    >
                      <Radio.Group className='flex flex-col gap-4 w-full'>
                        <div
                          onClick={() =>
                            form.setFieldValue('paymentOption', 'online')
                          }
                          className={twMerge(
                            'flex-1 flex items-start gap-0  rounded-xl p-4 cursor-pointer transition-colors duration-200 hover:bg-grayZl-925',
                            paymentOption === 'online'
                              ? 'bg-grayZl-925'
                              : 'bg-transparent',
                          )}
                        >
                          <Radio
                            value='online'
                            style={{ alignItems: 'flex-start' }}
                            className=''
                          ></Radio>
                          <PaymentAvailability />
                        </div>
                        <RenderIf condition={!isDigitalProduct && !isPreOrder && countryState.selectedCountry?.code == 'IN' && !hideCOD && !hasCodDisabledItem}>
                          <div
                            onClick={() =>
                              form.setFieldValue('paymentOption', 'cod')
                            }
                            className={twMerge(
                              'flex-1 rounded-xl p-4 cursor-pointer min-w-[180px] transition-colors duration-200 hover:bg-grayZl-925',
                              '[&_.ant-radio-inner]:border-grayZl-600',
                              '[&_.ant-radio-checked_.ant-radio-inner]:border-grayZl-600',
                              // '[&_.ant-radio-checked_.ant-radio-inner]:bg-grayZl-600',
                              paymentOption === 'cod'
                                ? 'bg-grayZl-925'
                                : 'bg-transparent',
                            )}
                          >
                            <Radio value='cod' className='text-grayZl-600'>Cash On Delivery (COD)</Radio>
                          </div>
                        </RenderIf>
                      </Radio.Group>
                    </Form.Item>
                  </>
                </CheckoutV2Card>
              </motion.div>

              <motion.div
                key='checkout-right'
                initial={{ opacity: 0, x: 30, filter: 'blur(4px)' }}
                animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
              >
                <CheckoutV2Card className='lg:min-w-[400px]  lg:w-[400px] w-full flex flex-col gap-4 '>
                  <RenderIf condition={twScreen.lg}>
                    <div className='p-4'>{steps}</div>
                  </RenderIf>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      id='checkout-v2-coupon-input'
                      className={inputClassName}
                      size={inputSize}
                      variant={inputVarient}
                      value={coupon}
                      onChange={(i) => setCoupon(i.target.value)}
                      placeholder='Enter your promo code'
                    />
                    <Button
                      id='checkout-v2-coupon-btn'
                      loading={couponLoading}
                      disabled={couponLoading}
                      size={inputSize}
                      variant='outlined'
                      onClick={applyCouponHandle}
                      style={{ boxShadow: 'none' }}
                      type='default'
                    >
                      Apply
                    </Button>
                  </Space.Compact>

                  <div
                    className={twMerge(
                      ' flex flex-col overflow-auto',
                      childCardClassName,
                    )}
                  >
                    <RenderIf
                      condition={!cartDetailsLoading && cart?.success === false}
                    >
                      <p className='text-sm'>Empty cart</p>
                    </RenderIf>
                    <RenderIf
                      fallBack={<Skeleton />}
                      condition={
                        cart?.data?.[0]?.cart_items !== 0 && !cartDetailsLoading
                      }
                    >
                      {cart?.data?.[0]?.cart_items?.map((i: any, index: any) => (
                        <div key={index} className='flex items-start  gap-4'>
                          <div className='flex items-start  gap-4'>
                            <div className='min-w-[90px] w-[90px] min-h-[90px] max-h-[90px] h-[90px]  p-1 flex items-center justify-center'>
                              <Image
                                className='w-auto h-full bg-contain'
                                src={i?.product_thumbnail_image || ''}
                                // src={BookDummy}
                                alt='Book Image'
                                width={100}
                                height={100}
                              />
                            </div>
                            <div>
                              <p className='text-sm text-grayZl-600'>
                                Qty : {i.quantity}
                              </p>
                              <p className=''>{i.product_name}</p>
                            </div>
                          </div>
                          <div>
                            <p className={twMerge('text-nowrap ', priceClassName)}>
                              {i?.currency_symbol + ' ' + i?.price + ' /-'}
                            </p>
                            <p
                              className={twMerge(
                                'text-nowrap line-through text-grayZl-600 text-sm font-semibold',
                                priceClassName,
                              )}
                            >
                              {i?.currency_symbol + ' ' + i?.stroked_price + ' /-'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </RenderIf>
                  </div>

                  <div
                    className={twMerge(
                      ' p-4 flex flex-col gap-4',
                      childCardClassName,
                    )}
                  >
                    <RenderIf fallBack={<Skeleton />} condition={!cartSummaryLoading && !cartDetailsLoading}>
                      <>
                        <div className='flex items-start justify-between'>
                          <p>Cart Total</p>
                          <p className={twMerge('', priceClassName)}>
                            {cartSummary?.currency_symbol}{' '}
                            {cartSummary?.product_regular_price}
                          </p>
                        </div>

                        {cartSummary?.coupon_code?.map((i: any, index: any) => (
                          <div key={index} className='flex items-start justify-between'>
                            <p>Coupon Applied ({i.coupon_code})</p>
                            <Button
                              loading={loadingRemoveCoupon}
                              disabled={loadingRemoveCoupon}
                              htmlType='button'
                              onClick={() => removeCouponHandle()}
                              type='text'
                              className={twMerge('text-primary px-0', priceClassName)}
                            >
                              REMOVE
                            </Button>
                          </div>
                        ))}

                        <div className='flex flex-col'>
                          <p>Digital Copy</p>
                          {cart?.data?.[0]?.cart_items?.map((i: any, index: number) => {
                            if (
                              i?.book_type === 'hardcover' &&
                              i?.digital_product?.name
                            ) {
                              return (
                                <div key={index} className='flex justify-between gap-2'>
                                  <p>{i?.digital_product?.name}</p>
                                  <div className='flex gap-2'>
                                    <p
                                      className={twMerge(
                                        'text-green-500',
                                        priceClassName,
                                      )}
                                    >
                                      FREE
                                    </p>
                                  </div>
                                </div>
                              );
                            }

                            return null;
                          })}
                        </div>
                        {/* <div className='border border-dashed'></div> */}
                        <RenderIf
                          condition={Boolean(
                            parseFloat(cartSummary?.discount?.replaceAll(',', '')),
                          )}
                        >
                          <div className='flex items-start justify-between'>
                            <p>Promo Code Discount</p>
                            <p className={twMerge('', priceClassName)}>
                              - {cart?.currency_symbol}{' '}
                              {parseFloat(cartSummary?.discount?.replaceAll(',', ''))}
                            </p>
                          </div>
                        </RenderIf>
                        <div className='flex items-start justify-between'>
                          <p>Shipping Charges</p>
                          <div className='flex gap-2'>
                            <p className={twMerge('text-green-500', priceClassName)}>
                              FREE
                            </p>
                          </div>
                        </div>
                        <RenderIf condition={Boolean(totalDiscount)}>
                          <div className='flex items-start justify-between'>
                            <p>Total Discount</p>
                            <div className='flex gap-2'>
                              <p className={twMerge('', priceClassName)}>
                                - {cartSummary?.currency_symbol}{' '}
                                {totalDiscount.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </RenderIf>
                        <div className='h-px bg-grayZl-800 w-full '></div>
                        <div className='flex items-start justify-between'>
                          <p>To Pay</p>
                          <p className={twMerge('', priceClassName)}>
                            {cartSummary?.currency_symbol} {cartSummary?.grand_total}
                          </p>
                        </div>
                      </>
                    </RenderIf>
                  </div>

                  <Form.Item prefixCls={formItemPrefixCls} className='m-0 p-0 hidden md:block'>
                    <Button
                      loading={loading}
                      disabled={loading}
                      className='w-full'
                      size='large'
                      icon={<LockFilled />}
                      type='primary'
                      htmlType='submit'
                    >
                      Proceed to Pay
                    </Button>
                  </Form.Item>
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='phoneCountryCode'
                    hidden
                    initialValue='in'
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='phonePrefix'
                    hidden
                    initialValue='91'
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='countryId'
                    hidden
                    initialValue={defaultCountry.id}
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='billingCountryId'
                    hidden
                    initialValue={defaultCountry.id}
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='stateId'
                    hidden
                    initialValue={null}
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='billingStateId'
                    hidden
                    initialValue={null}
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='billingCityId'
                    hidden
                    initialValue={null}
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='cityId'
                    hidden
                    initialValue={null}
                  />
                </CheckoutV2Card>
              </motion.div>
            </div>
          </AnimatePresence>
          <StickyPayButton
            loading={loading}
            disabled={loading}
            currencySymbol={cartSummary?.currency_symbol}
            grandTotal={cartSummary?.grand_total}
          />
        </Form>
        <AddressModal
          isBillingButton={false}
          isSelectButton={false}
          showDefaultButton
          onSetDefault={async (a) => {
            await updateDefaultAddress(a.id);
            // Refresh address list so the (Default) badge updates
            const updated = await getAddresses();
            setAddresses(updated);
          }}
          onClickEdit={async (a) => {
            setAddressModal((prev) => ({
              ...prev,
              show: false,
              currentClickType: 'edit',
              addressForUpdate: a,
            }));
            // Also set countryId / stateId so the reactive useEffect watchers
            // (watchedCountryId, watchedStateId) are correctly seeded with the
            // address being edited. Without these, changing country in the edit
            // form would not trigger the states cascade properly.
            form.setFieldsValue({
              address: a.address,
              country: a.country_name,
              countryId: parseInt(a?.country_id || '101'),
              state: a.state_name,
              stateId: parseInt(a?.state_id || '0') || undefined,
              city: a.city_name,
              cityId: parseInt(a?.city_id || '0') || undefined,
              pincode: a.postal_code,
            });
            const states = await getStatesHandle(
              parseInt(a?.country_id || '101'),
            );
            const cities = await getCitiesHandle(parseInt(a?.state_id || '17'));

            if (states) {
              setStates(states);
            }

            if (cities) {
              setCities(cities);
            }
          }}
          onClickCreate={() => {
            setAddressModal((prev) => ({
              ...prev,
              show: false,
              currentClickType: 'create',
            }));
            form.setFieldsValue({
              address: null,
              country: null,
              state: null,
              city: null,
              pincode: null,
            });
          }}
          selectedAddress={address}
          onSelect={(a) => {
            setAddress(a);
            setAddressModal((prev) => ({
              ...prev,
              show: false,
              currentClickType: null,
            }));
          }}
          onCancel={() => setAddressModal((prev) => ({ ...prev, show: false }))}
          addresses={addresses}
          open={addressModal.show}
        />
        <AddressModal
          isBillingButton={true}
          isSelectButton={false}
          onClickEdit={async (a) => {
            setBillingAddressModal((prev) => ({
              ...prev,
              currentClickType: 'edit',
              addressForUpdate: a,
              show: false,
            }));
            setBillingAddress(a);
            form.setFieldsValue({
              billingAddress: a.address,
              billingCountry: a.country_name,
              billingState: a.state_name,
              billingCity: a.city_name,
              billingPincode: a.postal_code,
            });
            const states = await getStatesHandle(
              parseInt(a?.country_id || '101'),
            );
            const cities = await getCitiesHandle(parseInt(a?.state_id || '17'));

            if (states) {
              setBillingStates(states);
            }

            if (cities) {
              setBillingCities(cities);
            }
          }}
          onClickCreate={() => {
            setBillingAddressModal((prev) => ({
              ...prev,
              show: false,
              currentClickType: 'create',
            }));
            form.setFieldsValue({
              billingAddress: null,
              billingCountry: null,
              billingState: null,
              billingCity: null,
              billingPincode: null,
            });
          }}
          selectedAddress={billingAddress}
          onSelect={(a) => {
            setBillingAddress(a);
            setBillingAddressModal((prev) => ({
              ...prev,
              currentClickType: null,
            }));
          }}
          onCancel={() =>
            setBillingAddressModal((prev) => ({ ...prev, show: false }))
          }
          addresses={addresses}
          open={billingAddressModal.show}
        />
      </div>
      {/* <RenderIf condition={loading}>
        <div className='w-full h-full fixed top-0 left-0 bg-black/35 z-50'></div>
      </RenderIf> */}
      <CancelPaymentModal
        onDoNotPlaceOrderClick={
          () => {
            const eventParams: EventDetails = {
              event_name: 'cancel_order',
              source: 'global',
              source_type: 'orders',
              source_id: retryOrderData?.data?.id?.toString() as string,
              sub_source: null,
              sub_source_id: null,
              unit: retryOrderData?.data?.currency_code || 'INR',
              currency: retryOrderData?.data?.currency_code || 'INR',
              value: retryOrderData?.data?.grand_total,
              meta: {
                click_source: 'retry_popup',
                payment_mode:
                  retryOrderData?.data?.payment_type === 'razorpay' ? 'prepaid' : 'cod',
              },
            };

            logEvent({
              eventDetails: eventParams,
              userData: userData,
              pathname: router.pathname,
            });
            if (
              typeof mixpanel !== 'undefined' &&
              process.env.NEXT_PUBLIC_APP_ENV === 'production'
            ) {
              mixpanel.track(eventParams.event_name as string, eventParams);
            }
          }
        }
        open={cancelPaymentModal}
        // onCancel={() => setCancelPaymentModal(false)}
        isPreOrder={isPreOrder}
        isDigitalProduct={isDigitalProduct}
        retryOrder={retryOrderHandle}
        onCodClick={onCodClickHandle}
        convertCodLoading={convertCodLoading}
        isIndiaShipping={countryState.selectedCountry?.code?.toUpperCase() === 'IN'}
        hasNonCodItems={hasCodDisabledItem}
      />

      <RenderIf condition={loading}>
        <LoadingV2
          className='backdrop-blur-sm bg-black/35'
          loaderColor='white'
          messages={[
            'Almost there, thanks for your patience!',
            'Getting everything set up for you.',
            'Your content is on its way!',
            "Just a sec, making sure everything's perfect.",
            "Hold on, we're almost ready!",
            "Loading... we'll be with you shortly.",
            "Thanks for waiting, we're preparing your journey!",
          ]}
        />
      </RenderIf>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { cartId, buy_now, cn } = context.query;
  return {
    props: {
      cartId: (Array.isArray(cartId) ? cartId[0] : cartId) ?? null,
      buyNow: (Array.isArray(buy_now) ? buy_now[0] : buy_now) ?? null,
      cn: (Array.isArray(cn) ? cn[0] : cn) ?? null,
    },
  };
};

export default CheckoutV2;
